import { getWrestlerName, getWrestlerNameUpper, wrestlerMatchesSearch, sortWrestlersByName } from './wrestlerUtils';
import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import RecruitmentTab from './RecruitmentTab';
import { TITLES_DATABASE, TITLE_CATEGORIES } from './titlesDatabase';
import placeholderTitle from '../assets/Placeholder_Title.png';
import { MATCH_TYPES } from './matchTypesDatabase';
import placeholder from '../assets/placeholder.png';
import background from '../assets/Background_Test.jpg';
import STORYLINES, { buildVars, resolveText } from './storylinesDatabase';
import UnifiedWrestlerCard, { WrestlerCardStyles } from './WrestlerCard';
import SuperstarPicker from './SuperstarPicker';


// Dynamic wrestler render loader
const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const getTitleRender = (id) => id ? process.env.PUBLIC_URL + '/titles/' + id + '-render.png' : null;

const BRAND_COLORS = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };
const CLASS_COLORS = { 'Fighter': '#3498db', 'Bruiser': '#e74c3c', 'Cruiser': '#2ecc71', 'Specialist': '#9b59b6', 'Giant': '#e67e22' };
const STATUS_COLORS = { 'active': '#2ecc71', 'contract': '#3498db', 'local': '#f39c12', 'inactive': '#888' };
const XP_COLORS = { 0: '#555', 5: '#7f8c8d', 10: '#2ecc71', 15: '#3498db', 20: '#9b59b6', 25: '#d4af37' };

const PROMO_LEVELS = [1, 2, 3, 4, 5];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Anton&family=Imprima&family=Monda:wght@400;700&display=swap');
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .section-card:hover { outline: 2px solid rgba(255,255,255,0.3) !important; transform: translateY(-3px); }
  .section-card { transition: all 0.2s; cursor: pointer; }
  .row-hover:hover { background: rgba(255,255,255,0.06) !important; }
  .row-hover { transition: background 0.1s; cursor: pointer; }
  .wrestler-card:hover { outline: 1.5px solid rgba(255,255,255,0.35) !important; transform: translateY(-2px); }
  .wrestler-card { transition: all 0.18s; cursor: pointer; }
  .btn-wwe:hover { background: rgba(255,255,255,0.1) !important; border-color: rgba(255,255,255,0.6) !important; color: #fff !important; }
  .view-btn:hover { background: rgba(255,255,255,0.08) !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
  select option { background: #1a1a1a; color: #fff; }
`;

const S = {
  panel: { background: 'rgba(8,8,8,0.85)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', backdropFilter: 'blur(14px)' },
  title: { fontFamily: "'Anton', sans-serif", letterSpacing: '3px', color: '#fff' },
  mono: { fontFamily: "'Monda', sans-serif" },
  anton: { fontFamily: "'Anton', sans-serif" },
  btn: { fontFamily: "'Monda', sans-serif", fontSize: '0.55rem', letterSpacing: '1.5px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.6)', padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.15s' },
  input: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '7px 12px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none' },
};

// ── Icons ───────────────────────────────────────────────────────────
const getClassIcon = (cls) => process.env.PUBLIC_URL + '/icons/class-' + (cls || '').toLowerCase() + '.png';

const ClassIcon = ({ cls, size = 22 }) => (
  <img
    src={getClassIcon(cls)}
    alt={cls}
    title={cls}
    style={{ width: size + 'px', height: size + 'px', objectFit: 'contain', filter: `drop-shadow(0 0 3px ${CLASS_COLORS[cls] || '#444'}88)` }}
    onError={e => { e.target.style.display = 'none'; }}
  />
);

const getRoleIcon = (heel) => process.env.PUBLIC_URL + '/icons/role-' + (heel ? 'heel' : 'face') + '.png';

const RoleIcon = ({ heel, size = 22 }) => (
  <img
    src={getRoleIcon(heel)}
    alt={heel ? 'Heel' : 'Face'}
    title={heel ? 'Heel' : 'Face'}
    style={{ width: size + 'px', height: size + 'px', objectFit: 'contain' }}
    onError={e => { e.target.style.display = 'none'; }}
  />
);

const getXPBadge = (xp) => {
  const n = Number(xp) || 0;
  if (n >= 25) return process.env.PUBLIC_URL + '/icons/xp-badge-5.png';
  if (n >= 20) return process.env.PUBLIC_URL + '/icons/xp-badge-4.png';
  if (n >= 15) return process.env.PUBLIC_URL + '/icons/xp-badge-3.png';
  if (n >= 10) return process.env.PUBLIC_URL + '/icons/xp-badge-2.png';
  return process.env.PUBLIC_URL + '/icons/xp-badge-1.png';
};

const XpIcon = ({ xp, size = 28 }) => (
  <div title={'Ring XP: ' + xp} style={{ position: 'relative', width: size + 'px', height: size + 'px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <img src={getXPBadge(xp)} alt={'XP ' + xp} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
    <span style={{ position: 'absolute', fontFamily: "'Anton',sans-serif", fontSize: (size * 0.38) + 'px', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.9)', lineHeight: 1, pointerEvents: 'none' }}>{xp}</span>
  </div>
);

const PromoIcon = ({ level }) => (
  <div title={`Promo Level: ${level}/5`} style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
    {[1,2,3,4,5].map(i => (
      <div key={i} style={{ width: '4px', height: '12px', borderRadius: '2px', background: i <= (level || 0) ? '#d4af37' : 'rgba(255,255,255,0.1)' }} />
    ))}
  </div>
);

// ── Edit Superstar Popup ────────────────────────────────────────────
const EditWrestlerPopup = ({ wrestler, originalWrestler, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: getWrestlerName(wrestler, 'display'),
    status: wrestler.status || 'active',
    gender: wrestler.gender || 'M',
    fight_class: wrestler.fight_class || 'Fighter',
    fight_class_secondary: wrestler.fight_class_secondary || '',
    heel: wrestler.heel || false,
    starting_xp: wrestler.starting_xp || 0,
    popularity: wrestler.popularity || 0,
    promo_skill: wrestler.promo_skill || 1,
    match_types: wrestler.match_types || [],
    customRender: wrestler.customRender || null,
  });

  const [uploadingRender, setUploadingRender] = useState(false);
  const handleRenderUpload = async (e) => {
    var file = e.target.files[0];
    if (!file) return;
    setUploadingRender(true);
    try {
      var entityId = wrestler && wrestler.wrestler_id ? String(wrestler.wrestler_id) : 'edit_' + Date.now();
      var path = 'renders/shared/wrestler/' + entityId + '_' + Date.now();
      var storageRef = ref(storage, path);
      var snapshot = await uploadBytes(storageRef, file);
      var url = await getDownloadURL(snapshot.ref);
      setForm(function(p) { return { ...p, customRender: url, customRenderPath: path }; });
    } catch (err) {
      console.error('Upload failed:', err);
      var reader = new FileReader();
      reader.onload = function(ev) { setForm(function(p) { return { ...p, customRender: ev.target.result }; }); };
      reader.readAsDataURL(file);
    } finally {
      setUploadingRender(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      <div style={{ ...S.panel, width: '100%', maxWidth: '580px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: '24px', overflowY: 'auto' }}>
        <div style={{ ...S.title, fontSize: '1rem', marginBottom: '20px', letterSpacing: '2px' }}>EDIT SUPERSTAR</div>

        <div style={{ display: 'flex', gap: '16px' }}>
          {/* Render */}
          <div style={{ width: '120px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>RENDER</div>
            <div onClick={() => document.getElementById('editRenderInGame').click()}
              style={{ width: '120px', height: '150px', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
              <img src={form.customRender || (form.wrestler_id ? getWrestlerRender(form) : placeholder)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'top' }} />
            </div>
            <input id="editRenderInGame" type="file" accept="image/png" style={{ display: 'none' }} onChange={handleRenderUpload} />
            {form.customRender && form.customRender !== wrestler.originalRender && (
              <button className="btn-wwe" style={{ ...S.btn, fontSize: '0.4rem', padding: '3px 6px', borderColor: 'rgba(231,76,60,0.4)', color: 'rgba(231,76,60,0.7)' }}
                onClick={() => setForm(p => ({ ...p, customRender: null }))}>✕ Remove</button>
            )}
          </div>

          {/* Fields */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>NAME</div>
              <input style={{ ...S.input, width: '100%' }} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>STATUS</div>
                <select style={{ ...S.input, width: '100%', cursor: 'pointer' }} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="contract">Contract</option>
                  <option value="local">Local</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>GENDER</div>
                <select style={{ ...S.input, width: '100%', cursor: 'pointer' }} value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>PRIMARY CLASS</div>
                <select style={{ ...S.input, width: '100%', cursor: 'pointer' }} value={form.fight_class} onChange={e => setForm(p => ({ ...p, fight_class: e.target.value }))}>
                  {['Fighter', 'Bruiser', 'Cruiser', 'Specialist', 'Giant'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>SECONDARY CLASS</div>
                <select style={{ ...S.input, width: '100%', cursor: 'pointer' }} value={form.fight_class_secondary} onChange={e => setForm(p => ({ ...p, fight_class_secondary: e.target.value }))}>
                  <option value="">None</option>
                  {['Fighter', 'Bruiser', 'Cruiser', 'Specialist', 'Giant'].filter(c => c !== form.fight_class).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>ROLE</div>
                <select style={{ ...S.input, width: '100%', cursor: 'pointer' }} value={form.heel ? 'heel' : 'face'} onChange={e => setForm(p => ({ ...p, heel: e.target.value === 'heel' }))}>
                  <option value="face">Face</option>
                  <option value="heel">Heel</option>
                </select>
              </div>
              <div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>RING XP</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number" min="0" max="25"
                    style={{ ...S.input, width: '60px', textAlign: 'center' }}
                    value={form.starting_xp}
                    onChange={e => {
                      const v = Math.min(25, Math.max(0, Number(e.target.value) || 0));
                      setForm(p => ({ ...p, starting_xp: v }));
                    }}
                  />
                  <XpIcon xp={form.starting_xp} size={28} />
                </div>
              </div>
            </div>

            {/* Promo Level */}
            <div>
              <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '6px' }}>PROMO LEVEL</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {PROMO_LEVELS.map(l => (
                  <button key={l} onClick={() => setForm(p => ({ ...p, promo_skill: l }))}
                    style={{ ...S.btn, padding: '5px 12px', fontSize: '0.55rem', borderColor: form.promo_skill === l ? '#d4af37' : 'rgba(255,255,255,0.15)', color: form.promo_skill === l ? '#d4af37' : 'rgba(255,255,255,0.35)', background: form.promo_skill === l ? 'rgba(212,175,55,0.1)' : 'transparent' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Match Types — 2 dropdowns */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[0, 1].map(idx => (
                <div key={idx}>
                  <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' }}>
                    MATCH TYPE {idx + 1}
                  </div>
                  <select style={{ ...S.input, width: '100%', cursor: 'pointer' }}
                    value={(form.match_types || [])[idx] || ''}
                    onChange={e => {
                      const val = e.target.value;
                      const types = [...(form.match_types || [])];
                      types[idx] = val;
                      setForm(p => ({ ...p, match_types: types.filter(Boolean) }));
                    }}>
                    <option value="">None</option>
                    {MATCH_TYPES.filter(m => m.id !== 'normal').map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            {originalWrestler && (
              <button className="btn-wwe" style={{ ...S.btn, borderColor: 'rgba(241,196,15,0.4)', color: 'rgba(241,196,15,0.7)', fontSize: '0.48rem' }}
                onClick={() => setForm({ name: getWrestlerName(originalWrestler, 'display'), status: originalWrestler.status, gender: originalWrestler.gender, fight_class: originalWrestler.fight_class, fight_class_secondary: '', heel: originalWrestler.heel, starting_xp: originalWrestler.starting_xp, popularity: originalWrestler.popularity, promo_skill: originalWrestler.promo_skill || 1, match_types: [], customRender: null })}>
                ↺ RESET TO DEFAULT
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn-wwe" style={S.btn} onClick={onClose}>CANCEL</button>
            <button className="btn-wwe" style={{ ...S.btn, borderColor: 'rgba(255,255,255,0.55)', color: '#fff' }} onClick={() => onSave(form)}>SAVE</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Rivalry level colors ─────────────────────────────────────────────
const RIVALRY_LVL_COLORS = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#9b59b6' };

// ── Wrestler Card (grid view) ───────────────────────────────────────
const WrestlerCard = ({ w, brandColor, onClick, onRelease, onInjure, onSuspend, rivalries, champions }) => {
  var wid = String(w.wrestler_id);

  // -- Rival info
  var rivalry = (rivalries || []).find(function(r) {
    return (r.side1 || []).some(function(id) { return String(id) === wid; }) ||
           (r.side2 || []).some(function(id) { return String(id) === wid; });
  });
  var rivalColor = rivalry ? (RIVALRY_LVL_COLORS[rivalry.level] || '#3498db') : null;
  var rivalNames = [];
  if (rivalry) {
    var inS1 = (rivalry.side1 || []).some(function(id) { return String(id) === wid; });
    var rivalIds = inS1 ? (rivalry.side2 || []) : (rivalry.side1 || []);
    rivalNames = rivalIds.map(function(id) {
      var rw = w._roster && w._roster.find(function(x) { return String(x.wrestler_id) === String(id); });
      return rw ? getWrestlerName(rw, 'display') : null;
    }).filter(Boolean);
  }

  // -- Champion info
  // champions = { slotKey: [wrestlerObject, ...] } + champions.slots = slot definitions
  var champMap2 = (champions && champions.champions) || {};
  var champSlots2 = (champions && champions.slots) || [];
  var champSlot = null;
  Object.keys(champMap2).forEach(function(slotKey) {
    var val = champMap2[slotKey];
    var wrestlers = Array.isArray(val) ? val : (val ? [val] : []);
    var isChamp = wrestlers.some(function(cw) {
      if (!cw) return false;
      // val can be wrestler object or wrestler_id
      if (typeof cw === 'object') return String(cw.wrestler_id) === wid;
      return String(cw) === wid;
    });
    if (isChamp) {
      champSlot = champSlots2.find(function(s) { return s.key === slotKey; }) || { key: slotKey };
    }
  });
  var isChampion = !!champSlot;

  // -- Injured
  var isInjured = w.injured && w.injuryWeeksLeft > 0;
  
  // -- Suspended
  var isSuspended = w.suspended && w.suspensionWeeksLeft > 0;

  // -- Top bar color priority: injured > suspended > champion > rival
  var topBarColor = isInjured ? '#e74c3c' : isSuspended ? '#9b59b6' : isChampion ? '#d4af37' : rivalColor;

  return (
    <div className="wrestler-card" onClick={onClick}
      style={{ ...S.panel, overflow: 'hidden', outline: '1px solid transparent', position: 'relative', border: '1px solid ' + (topBarColor ? topBarColor + '44' : 'rgba(255,255,255,0.08)') }}>

      {/* Top status bar */}
      {topBarColor && <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + topBarColor + ', transparent)', flexShrink: 0 }} />}

      {/* Release button */}
      <button onClick={e => { e.stopPropagation(); onRelease && onRelease(w); }} style={{ position: 'absolute', top: '8px', left: '5px', zIndex: 3, background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(231,76,60,0.5)', color: 'rgba(231,76,60,0.8)', borderRadius: '3px', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.45rem', lineHeight: 1 }}>✕</button>
      {/* Injury toggle button */}
      <button onClick={e => { e.stopPropagation(); onInjure && onInjure(w); }} style={{ position: 'absolute', top: '8px', left: '27px', zIndex: 3, background: isInjured ? 'rgba(231,76,60,0.4)' : 'rgba(0,0,0,0.6)', border: '1px solid ' + (isInjured ? 'rgba(231,76,60,0.8)' : 'rgba(255,255,255,0.2)'), borderRadius: '3px', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
        <img src={process.env.PUBLIC_URL + '/icons/injury.png'} alt="" style={{ width: '12px', height: '12px', objectFit: 'contain', opacity: isInjured ? 1 : 0.5 }} onError={function(e) { e.target.style.display='none'; }} />
      </button>
      {/* Suspension toggle button */}
      <button onClick={e => { e.stopPropagation(); onSuspend && onSuspend(w); }} style={{ position: 'absolute', top: '8px', left: '49px', zIndex: 3, background: isSuspended ? 'rgba(155,89,182,0.4)' : 'rgba(0,0,0,0.6)', border: '1px solid ' + (isSuspended ? 'rgba(155,89,182,0.8)' : 'rgba(255,255,255,0.2)'), borderRadius: '3px', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.55rem', color: isSuspended ? '#fff' : 'rgba(255,255,255,0.5)', padding: 0, lineHeight: 1 }} title="Suspension">
        🚫
      </button>

      {/* Custom badge */}
      {w.isCustom && <div style={{ position: 'absolute', top: '10px', left: '28px', zIndex: 2, ...S.mono, fontSize: '0.32rem', color: '#d4af37', background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '2px', padding: '1px 3px' }}>CUSTOM</div>}

      {/* Render area */}
      <div style={{ height: '220px', overflow: 'hidden', position: 'relative', background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)' }}>
        <img src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
          style={{ position: 'absolute', bottom: '-15%', left: '50%', transform: 'translateX(-50%)', height: '280px', width: 'auto', objectFit: 'contain', objectPosition: 'bottom', opacity: (isInjured || isSuspended) ? 0.5 : 1 }} />

        {/* Injured banner */}
        {isInjured && (
          <div style={{ position: 'absolute', bottom: '0', left: 0, right: 0, background: 'rgba(231,76,60,0.85)', padding: '3px 6px', textAlign: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#fff', letterSpacing: '1px' }}>
            INJURED — {w.injuryWeeksLeft} WK{w.injuryWeeksLeft > 1 ? 'S' : ''}
          </div>
        )}
        
        {/* Suspended banner */}
        {isSuspended && !isInjured && (
          <div style={{ position: 'absolute', bottom: '0', left: 0, right: 0, background: 'rgba(155,89,182,0.85)', padding: '3px 6px', textAlign: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#fff', letterSpacing: '1px' }}>
            🚫 SUSPENDED — {w.suspensionWeeksLeft} WK{w.suspensionWeeksLeft > 1 ? 'S' : ''}
          </div>
        )}

        {/* Rival banner (shown only if not injured/suspended) */}
        {rivalry && rivalNames.length > 0 && !isInjured && !isSuspended && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: rivalColor + 'cc', padding: '3px 6px', textAlign: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#fff', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            ⚔ {rivalNames.join(' & ')}
          </div>
        )}

        {/* Both injured + rival */}
        {rivalry && rivalNames.length > 0 && isInjured && (
          <div style={{ position: 'absolute', bottom: '20px', left: 0, right: 0, background: rivalColor + 'cc', padding: '3px 6px', textAlign: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: '#fff', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            ⚔ {rivalNames.join(' & ')}
          </div>
        )}
      </div>

      {/* Info bar */}
      <div style={{ padding: '8px 10px 10px', borderTop: '1px solid ' + (topBarColor ? topBarColor + '33' : brandColor + '33') }}>
        <div style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1px', color: '#fff', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getWrestlerNameUpper(w, 'display')}</div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ClassIcon cls={w.fight_class} />
          {w.fight_class_secondary && <ClassIcon cls={w.fight_class_secondary} />}
          <RoleIcon heel={w.heel} />
          <XpIcon xp={w.starting_xp || 0} />
          {(w.promo_skill || 1) > 0 && <PromoIcon level={w.promo_skill || 1} />}
          {/* Injury icon */}
          {isInjured && (
            <img src={process.env.PUBLIC_URL + '/icons/injury.png'} alt="Injured"
              style={{ width: '18px', height: '18px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
          )}
          {/* Champion belt icon */}
          {isChampion && champSlot && (
            <img src={(champSlot.titleOverride && champSlot.titleOverride.render) || getTitleRender(champSlot.titleOverride && champSlot.titleOverride.id) || placeholderTitle} alt="Champion"
              style={{ height: '20px', objectFit: 'contain', marginLeft: 'auto', filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.9))' }} onError={e => { e.target.src = placeholderTitle; }} />
          )}
        </div>

        {/* Releasing badge */}
        {w.releasing && (
          <div style={{ marginTop: '4px', fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: '#e74c3c', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '2px', padding: '1px 5px', letterSpacing: '0.5px' }}>
            ⚠ LAST WEEK
          </div>
        )}

        {/* Contract info */}
        {w.contractEnd
          ? <div style={{ marginTop: '4px', fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: w.contractEnd <= (w._currentWeek || 99) + 2 ? '#e74c3c' : 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>
              📋 Until W{w.contractEnd}
            </div>
          : w.draftPick
          ? <div style={{ marginTop: '4px', fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.5px' }}>
              📋 Until end of season
            </div>
          : null
        }
      </div>
    </div>
  );
};

// ── Actual Roster Page ──────────────────────────────────────────────

// ── Tag Teams Tab ─────────────────────────────────────────────────────
const TagTeamsTab = ({ gameData, roster, brandColor, onUpdateGameData }) => {
  var tagTeams = (gameData && gameData.tagTeams) || [];
  var [showCreate, setShowCreate] = useState(false);
  var [editTarget, setEditTarget] = useState(null);
  var [teamName, setTeamName] = useState('');
  var [nameIsAuto, setNameIsAuto] = useState(false);
  var [partner1, setPartner1] = useState(null);
  var [partner2, setPartner2] = useState(null);
  var [pickerFor, setPickerFor] = useState(null); // 'p1' | 'p2' | null

  // Auto-clean: remove tag teams where either partner is no longer on the roster
  useEffect(function() {
    if (!roster || !tagTeams.length) return;
    var rosterIds = new Set(roster.map(function(w) { return w.wrestler_id; }));
    var cleaned = tagTeams.filter(function(t) {
      return t.partner1 && rosterIds.has(t.partner1.wrestler_id)
          && t.partner2 && rosterIds.has(t.partner2.wrestler_id);
    });
    if (cleaned.length !== tagTeams.length) {
      onUpdateGameData && onUpdateGameData({ ...gameData, tagTeams: cleaned });
    }
  }, [roster]); // eslint-disable-line react-hooks/exhaustive-deps

  var usedIds = tagTeams.flatMap(function(t) { return [t.partner1 && t.partner1.wrestler_id, t.partner2 && t.partner2.wrestler_id]; }).filter(Boolean);
  var editIds = editTarget ? [editTarget.partner1 && editTarget.partner1.wrestler_id, editTarget.partner2 && editTarget.partner2.wrestler_id].filter(Boolean) : [];
  var excludeP1 = [partner2 && partner2.wrestler_id, ...usedIds.filter(id => !editIds.includes(id))].filter(Boolean);
  var excludeP2 = [partner1 && partner1.wrestler_id, ...usedIds.filter(id => !editIds.includes(id))].filter(Boolean);

  // Auto-detect team name when both partners are set
  var resolveAutoName = function(p1, p2) {
    if (!p1 || !p2) return null;
    var n1 = p1.team_name && p1.team_name.trim();
    var n2 = p2.team_name && p2.team_name.trim();
    if (n1 && n2 && n1 === n2) return n1;
    return null;
  };

  var handleSetPartner = function(slot, w) {
    var newP1 = slot === 'p1' ? w : partner1;
    var newP2 = slot === 'p2' ? w : partner2;
    if (slot === 'p1') setPartner1(w); else setPartner2(w);
    setPickerFor(null);
    // Auto-name only if name hasn't been manually edited
    var auto = resolveAutoName(newP1, newP2);
    if (auto && (!teamName || nameIsAuto)) {
      setTeamName(auto);
      setNameIsAuto(true);
    } else if (!auto && nameIsAuto) {
      setTeamName('');
      setNameIsAuto(false);
    }
  };

  var handleSave = function() {
    if (!partner1 || !partner2) return;
    var newTeam = { id: editTarget ? editTarget.id : Date.now().toString(), name: teamName.trim(), partner1, partner2 };
    var newTeams = editTarget
      ? tagTeams.map(function(t) { return t.id === editTarget.id ? newTeam : t; })
      : [...tagTeams, newTeam];
    onUpdateGameData && onUpdateGameData({ ...gameData, tagTeams: newTeams });
    setShowCreate(false); setEditTarget(null); setTeamName(''); setPartner1(null); setPartner2(null); setNameIsAuto(false);
  };

  var handleDelete = function(id) {
    var newTeams = tagTeams.filter(function(t) { return t.id !== id; });
    onUpdateGameData && onUpdateGameData({ ...gameData, tagTeams: newTeams });
  };

  var openEdit = function(team) {
    setEditTarget(team); setTeamName(team.name || ''); setPartner1(team.partner1); setPartner2(team.partner2); setNameIsAuto(false); setShowCreate(true);
  };

  var openCreate = function() {
    setEditTarget(null); setTeamName(''); setPartner1(null); setPartner2(null); setNameIsAuto(false); setShowCreate(true);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Superstar picker overlay */}
      {pickerFor && (
        <SuperstarPicker
          roster={roster}
          alreadyPicked={pickerFor === 'p1' ? excludeP1.map(id => ({ wrestler_id: id })) : excludeP2.map(id => ({ wrestler_id: id }))}
          onSelect={w => handleSetPartner(pickerFor, w)}
          onClose={() => setPickerFor(null)}
          title={pickerFor === 'p1' ? 'SELECT PARTNER 1' : 'SELECT PARTNER 2'}
          showSynergy={false}
          rivalries={[]}
        />
      )}

      {/* Create / Edit Modal */}
      {showCreate && !pickerFor && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}>
          <div style={{ background: 'rgba(8,8,8,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '28px 32px', width: '100%', maxWidth: '520px' }}>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#fff', marginBottom: '20px' }}>
              {editTarget ? 'EDIT TAG TEAM' : 'CREATE TAG TEAM'}
            </div>

            {/* Partners — big render buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
              {[{ key: 'p1', label: 'PARTNER 1', val: partner1 }, { key: 'p2', label: 'PARTNER 2', val: partner2 }].map(function(slot, idx) { return (
                <React.Fragment key={slot.key}>
                  {idx === 1 && <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', color: brandColor, textAlign: 'center' }}>&</div>}
                  <div onClick={() => setPickerFor(slot.key)}
                    style={{ border: '1px solid ' + (slot.val ? brandColor + '77' : 'rgba(255,255,255,0.12)'), borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', background: slot.val ? brandColor + '08' : 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '160px', justifyContent: 'center', transition: 'all 0.15s' }}>
                    {slot.val ? (
                      <>
                        <div style={{ width: '100%', height: '150px', position: 'relative', overflow: 'hidden', background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)' }}>
                          <img src={getWrestlerRender(slot.val) || placeholder} alt=""
                            style={{ position: 'absolute', bottom: '-5%', left: '50%', transform: 'translateX(-50%)', height: '175px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.9))' }} />
                        </div>
                        <div style={{ padding: '8px 8px 4px', textAlign: 'center' }}>
                          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.56rem', color: '#fff', letterSpacing: '0.5px' }}>{getWrestlerNameUpper(slot.val, 'display')}</div>
                          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>tap to change</div>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '20px 10px' }}>+ SELECT<br/>{slot.label}</div>
                    )}
                  </div>
                </React.Fragment>
              ); })}
            </div>

            {/* Team name */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px' }}>TEAM NAME</div>
                {nameIsAuto && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: brandColor, background: brandColor + '18', border: '1px solid ' + brandColor + '44', borderRadius: '3px', padding: '1px 6px', letterSpacing: '0.5px' }}>AUTO</div>}
              </div>
              <input value={teamName} onChange={e => { setTeamName(e.target.value); setNameIsAuto(false); }} placeholder="e.g. The Usos..."
                style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid ' + (nameIsAuto ? brandColor + '44' : 'rgba(255,255,255,0.15)'), borderRadius: '6px', padding: '8px 12px', color: '#fff', fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', outline: 'none' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowCreate(false); setEditTarget(null); setPickerFor(null); setNameIsAuto(false); }}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '8px 18px', borderRadius: '5px', cursor: 'pointer' }}>CANCEL</button>
              <button onClick={handleSave} disabled={!partner1 || !partner2}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: partner1 && partner2 ? brandColor + '18' : 'transparent', border: '1px solid ' + (partner1 && partner2 ? brandColor : 'rgba(255,255,255,0.1)'), color: partner1 && partner2 ? brandColor : 'rgba(255,255,255,0.25)', padding: '8px 18px', borderRadius: '5px', cursor: partner1 && partner2 ? 'pointer' : 'not-allowed' }}>
                {editTarget ? 'SAVE' : 'CREATE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px' }}>{tagTeams.length} TAG TEAM{tagTeams.length !== 1 ? 'S' : ''}</div>
        <button onClick={openCreate}
          style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: brandColor + '18', border: '1px solid ' + brandColor + '66', color: brandColor, padding: '6px 16px', borderRadius: '5px', cursor: 'pointer' }}>
          + CREATE TEAM
        </button>
      </div>

      {/* Teams grid — cards */}
      {tagTeams.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', color: 'rgba(255,255,255,0.2)' }}>
          No tag teams yet
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
          {tagTeams.map(function(team) { return (
            <div key={team.id} style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Renders area */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '140px', background: 'radial-gradient(ellipse at bottom, ' + brandColor + '18 0%, transparent 70%)', position: 'relative', overflow: 'hidden' }}>
                <img src={getWrestlerRender(team.partner1) || placeholder} alt="" style={{ height: '130px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.9))', position: 'relative', zIndex: 1, transform: 'rotate(-4deg) translateX(10px)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.4rem', color: brandColor, zIndex: 2, flexShrink: 0, margin: '0 4px', paddingBottom: '12px' }}>&</div>
                <img src={getWrestlerRender(team.partner2) || placeholder} alt="" style={{ height: '130px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.9))', position: 'relative', zIndex: 1, transform: 'rotate(4deg) translateX(-10px)' }} onError={e => { e.target.style.display='none'; }} />
              </div>
              {/* Info footer */}
              <div style={{ padding: '10px 14px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                {team.name && (
                  <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.72rem', letterSpacing: '1.5px', color: '#fff', textAlign: 'center' }}>{team.name.toUpperCase()}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center' }}>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.55)' }}>{team.partner1 && getWrestlerName(team.partner1, 'display')}</div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: brandColor }}>·</div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.55)' }}>{team.partner2 && getWrestlerName(team.partner2, 'display')}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'center' }}>
                  <button onClick={() => openEdit(team)}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer' }}>EDIT</button>
                  <button onClick={() => handleDelete(team.id)}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', background: 'transparent', border: '1px solid rgba(231,76,60,0.3)', color: 'rgba(231,76,60,0.6)', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer' }}>DELETE</button>
                </div>
              </div>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
};




// ── Manual Suspension Modal ──────────────────────────────────────────
const SuspensionModal = ({ wrestler, brandColor, onConfirm, onClear, onClose }) => {
  var isSuspended = wrestler && wrestler.suspended && wrestler.suspensionWeeksLeft > 0;
  var isAbsent = wrestler && wrestler.absent;
  var currentType = (wrestler && wrestler.suspensionType) || (isAbsent ? 'absence' : 'suspension');
  var [type, setType] = React.useState(currentType);
  var [weeks, setWeeks] = React.useState(wrestler && wrestler.suspensionWeeksLeft > 0 ? wrestler.suspensionWeeksLeft : 2);
  var [reason, setReason] = React.useState((wrestler && wrestler.suspensionReason) || '');
  var isActive = isSuspended || isAbsent;
  var accentColor = type === 'absence' ? '#1abc9c' : '#9b59b6';
  var typeLabel = type === 'absence' ? 'ABSENCE' : 'SUSPENSION';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(8,8,8,0.98)', border: '1px solid ' + accentColor + '66', borderRadius: '10px', width: '400px', overflow: 'hidden' }}
        onClick={function(e) { e.stopPropagation(); }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + accentColor + '33', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.7rem', letterSpacing: '2px', color: accentColor }}>{isActive ? 'UPDATE ' + typeLabel : 'ADD ' + typeLabel}</div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{wrestler && getWrestlerName(wrestler, 'display')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', borderRadius: '6px', width: '26px', height: '26px', fontSize: '0.7rem' }}>✕</button>
        </div>
        <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Type selector */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>TYPE</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[{ id: 'suspension', label: '🚫 SUSPENSION', color: '#9b59b6' }, { id: 'absence', label: '⏸ ABSENCE', color: '#1abc9c' }].map(function(opt) {
                var isSel = type === opt.id;
                return (
                  <button key={opt.id} onClick={function() { setType(opt.id); }}
                    style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', letterSpacing: '0.5px', background: isSel ? opt.color + '18' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (isSel ? opt.color : 'rgba(255,255,255,0.12)'), color: isSel ? opt.color : 'rgba(255,255,255,0.4)', padding: '7px 10px', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.15s' }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Duration — free input */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>DURATION (WEEKS)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="number" min="1" max="52" value={weeks} onChange={function(e) { setWeeks(Math.max(1, Number(e.target.value) || 1)); }}
                style={{ width: '90px', background: 'rgba(255,255,255,0.04)', border: '1px solid ' + accentColor + '55', borderRadius: '4px', padding: '8px 12px', fontFamily: "'Anton',sans-serif", fontSize: '0.8rem', color: accentColor, outline: 'none', textAlign: 'center' }} />
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.3)' }}>week{weeks > 1 ? 's' : ''} out</div>
              {/* Quick presets */}
              <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                {[1, 2, 4, 8].map(function(n) { return (
                  <button key={n} onClick={function() { setWeeks(n); }}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', background: weeks === n ? accentColor + '22' : 'transparent', border: '1px solid ' + (weeks === n ? accentColor : 'rgba(255,255,255,0.12)'), color: weeks === n ? accentColor : 'rgba(255,255,255,0.35)', padding: '3px 7px', borderRadius: '3px', cursor: 'pointer' }}>{n}</button>
                ); })}
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>REASON <span style={{ color: 'rgba(255,255,255,0.2)' }}>· OPTIONAL</span></div>
            <input type="text" value={reason} onChange={function(e) { setReason(e.target.value); }}
              placeholder={type === 'absence' ? 'e.g. Personal reasons, filming, injury recovery...' : 'e.g. Wellness violation, disciplinary...'}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '8px 10px', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: '#fff', outline: 'none' }} />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
            {isActive && (
              <button onClick={onClear}
                style={{ flex: 1, fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1.5px', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.4)', color: '#2ecc71', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
                ✓ LIFT
              </button>
            )}
            <button onClick={function() { onConfirm(weeks, reason, type); }}
              style={{ flex: 2, fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1.5px', background: accentColor + '18', border: '1px solid ' + accentColor, color: accentColor, padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
              {isActive ? 'UPDATE' : (type === 'absence' ? 'SET ABSENCE' : 'SUSPEND')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Unavailable Tab (injured + suspended + absent) ────────────────────
const UnavailableTab = ({ roster, brandColor, onInjure, onSuspend, currentWeek }) => {
  var unavailable = roster.filter(function(w) {
    return (w.injured && w.injuryWeeksLeft > 0) ||
           (w.suspended && w.suspensionWeeksLeft > 0) ||
           (w.absent && w.suspensionWeeksLeft > 0);
  });

  if (unavailable.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', opacity: 0.2, marginBottom: '12px' }}>✓</div>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)' }}>All superstars available</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', overflowY: 'auto', flex: 1, alignContent: 'flex-start' }}>
      {unavailable.map(function(w) {
        var isInjured = w.injured && w.injuryWeeksLeft > 0;
        return (
          <UnifiedWrestlerCard
            key={w.wrestler_id}
            wrestler={w}
            brandColor={brandColor}
            variant="roster"
            showActionButtons={false}
            currentWeek={currentWeek}
            onInjure={isInjured ? () => onInjure && onInjure(w) : undefined}
            onSuspend={!isInjured ? () => onSuspend && onSuspend(w) : undefined}
            onClick={isInjured ? () => onInjure && onInjure(w) : () => onSuspend && onSuspend(w)}
          />
        );
      })}
    </div>
  );
};


// ── Manual Injury Modal ──────────────────────────────────────────────
const InjuryModal = ({ wrestler, brandColor, onConfirm, onClear, onClose }) => {
  var [weeks, setWeeks] = React.useState(wrestler && wrestler.injuryWeeksLeft > 0 ? wrestler.injuryWeeksLeft : 4);
  var isInjured = wrestler && wrestler.injured && wrestler.injuryWeeksLeft > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(8,8,8,0.98)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: '10px', width: '320px', overflow: 'hidden' }}
        onClick={function(e) { e.stopPropagation(); }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, #e74c3c, transparent)' }} />
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#fff', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={process.env.PUBLIC_URL + '/icons/injury.png'} alt="" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
            INJURY
          </div>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: '#e74c3c', marginBottom: '16px' }}>
            {getWrestlerNameUpper(wrestler, 'display')}
          </div>

          {isInjured ? (
            <>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
                Currently injured — <span style={{ color: '#e74c3c' }}>{wrestler.injuryWeeksLeft} week{wrestler.injuryWeeksLeft > 1 ? 's' : ''} remaining</span>
              </div>
              {/* Quick reduce buttons */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
                {[1, 2, 4].map(function(n) { return (
                  <button key={n} onClick={function() { onConfirm(Math.max(0, (wrestler.injuryWeeksLeft || 0) - n) || 0, true); }}
                    style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', padding: '7px 0', borderRadius: '4px', cursor: 'pointer' }}>
                    −{n}W
                  </button>
                ); })}
                <button onClick={onClear}
                  style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1px', background: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.35)', color: '#2ecc71', padding: '7px 0', borderRadius: '4px', cursor: 'pointer' }}>
                  CLEAR
                </button>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '0 0 16px' }} />
            </>
          ) : null}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.5)' }}>{isInjured ? 'Update duration' : 'Duration'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button onClick={function() { setWeeks(function(w) { return Math.max(1, w - 1); }); }}
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '4px', width: '28px', height: '28px', cursor: 'pointer', fontFamily: "'Monda',sans-serif", fontSize: '0.8rem' }}>−</button>
              <input type="number" min="1" max="52" value={weeks}
                onChange={function(e) { setWeeks(Math.max(1, Math.min(52, Number(e.target.value) || 1))); }}
                style={{ width: '52px', textAlign: 'center', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: '4px', padding: '5px', color: '#fff', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', outline: 'none' }} />
              <button onClick={function() { setWeeks(function(w) { return Math.min(52, w + 1); }); }}
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '4px', width: '28px', height: '28px', cursor: 'pointer', fontFamily: "'Monda',sans-serif", fontSize: '0.8rem' }}>+</button>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.4)' }}>week{weeks > 1 ? 's' : ''}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={function() { onConfirm(weeks); }}
              style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', letterSpacing: '1px', background: 'rgba(231,76,60,0.15)', border: '1px solid rgba(231,76,60,0.6)', color: '#e74c3c', padding: '8px', borderRadius: '5px', cursor: 'pointer' }}>
              {isInjured ? 'UPDATE' : 'SET INJURY'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActualRosterPage = ({ roster, brand, onBack, onUpdateRoster, gameData, confirmDestructive }) => {
  var [injuryTarget, setInjuryTarget] = React.useState(null);
  var [suspensionTarget, setSuspensionTarget] = React.useState(null);
  var rivalries = (gameData && gameData.rivalries) || [];
  var currentWeek = (gameData && gameData.week) || 1;
  const [subTab, setSubTab] = useState('roster'); // roster | tagteams
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseAmount, setReleaseAmount] = useState('');
  const brandColor = BRAND_COLORS[brand] || '#d4af37';
  const [view, setView] = useState('grid'); // grid | table
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterGender, setFilterGender] = useState('ALL');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [editTarget, setEditTarget] = useState(null);
  const [localRoster, setLocalRoster] = useState(roster || []);

  const filtered = localRoster
    .filter(w => w.status !== 'gm')
    .filter(w => filterStatus === 'ALL' || w.status === filterStatus.toLowerCase())
    .filter(w => filterGender === 'ALL' || w.gender === (filterGender === 'MALE' ? 'M' : 'F'))
    .filter(w => wrestlerMatchesSearch(w, search))
    .sort((a, b) => {
      if (sortKey === 'xp') { const d = (a.starting_xp || 0) - (b.starting_xp || 0); return sortDir === 'asc' ? d : -d; }
      if (sortKey === 'promo') { const d = (a.promo_skill || 0) - (b.promo_skill || 0); return sortDir === 'asc' ? d : -d; }
      let va = '', vb = '';
      if (sortKey === 'name') { va = getWrestlerName(a, 'display'); vb = getWrestlerName(b, 'display'); }
      else if (sortKey === 'class') { va = a.fight_class || ''; vb = b.fight_class || ''; }
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleSaveEdit = (updated) => {
    const newRoster = localRoster.map(w => w.wrestler_id === editTarget.wrestler_id ? { ...w, ...updated } : w);
    setLocalRoster(newRoster);
    onUpdateRoster?.({ ...gameData, roster: newRoster });
    setEditTarget(null);
  };

  // Release wrestler — with or without confirmation popup based on confirmDestructive setting
  const handleReleaseRequest = (w) => {
    if (confirmDestructive === false) {
      // Direct release, no popup
      const newRoster = (gameData.roster || []).filter(r => r.wrestler_id !== w.wrestler_id);
      const championsRaw = gameData.champions || {};
      const champMap = championsRaw.champions || {};
      const newChampMap = {};
      Object.entries(champMap).forEach(([key, champs]) => {
        if (!champs || champs.length === 0) return;
        const stillValid = champs.every(c => c && c.wrestler_id !== w.wrestler_id);
        if (stillValid) newChampMap[key] = champs;
      });
      const newChampions = { ...championsRaw, champions: newChampMap };
      setLocalRoster(newRoster);
      onUpdateRoster && onUpdateRoster({ ...gameData, roster: newRoster, champions: newChampions });
    } else {
      setReleaseTarget(w);
      setReleaseAmount('');
    }
  };

  const thStyle = (key) => ({
    ...S.mono, fontSize: '0.5rem', letterSpacing: '1.5px', color: sortKey === key ? '#fff' : 'rgba(255,255,255,0.35)',
    padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.07)',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', height: '100%' }}>
      <style>{css}</style>
      <style>{WrestlerCardStyles}</style>

      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: '2px', flexShrink: 0 }}>
        {[{ key: 'roster', label: 'ROSTER' }, { key: 'tagteams', label: 'TAG TEAMS' }, { key: 'unavailable', label: '⚠ UNAVAILABLE' }].map(function(t) { return (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', letterSpacing: '1.5px', background: 'transparent', border: 'none', borderBottom: '2px solid ' + (subTab === t.key ? brandColor : 'transparent'), color: subTab === t.key ? '#fff' : 'rgba(255,255,255,0.35)', padding: '6px 18px', cursor: 'pointer', transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ); })}
      </div>

      {/* Injury Modal */}
      {injuryTarget && (
        <InjuryModal
          wrestler={injuryTarget}
          brandColor={brandColor}
          onClose={() => setInjuryTarget(null)}
          onClear={() => {
            var updated = localRoster.map(function(r) { return r.wrestler_id === injuryTarget.wrestler_id ? { ...r, injured: false, injuryWeeksLeft: 0 } : r; });
            setLocalRoster(updated);
            onUpdateRoster && onUpdateRoster({ ...gameData, roster: updated });
            setInjuryTarget(null);
          }}
          onConfirm={function(weeks) {
            var isZero = weeks <= 0;
            var updated = localRoster.map(function(r) { return r.wrestler_id === injuryTarget.wrestler_id ? { ...r, injured: !isZero, injuryWeeksLeft: isZero ? 0 : weeks } : r; });
            setLocalRoster(updated);
            onUpdateRoster && onUpdateRoster({ ...gameData, roster: updated });
            setInjuryTarget(null);
          }}
        />
      )}

      {/* Suspension Modal */}
      {suspensionTarget && (
        <SuspensionModal
          wrestler={suspensionTarget}
          brandColor={brandColor}
          onClose={() => setSuspensionTarget(null)}
          onClear={() => {
            var updated = localRoster.map(function(r) { return r.wrestler_id === suspensionTarget.wrestler_id ? { ...r, suspended: false, absent: false, suspensionWeeksLeft: 0, suspensionReason: '', suspensionType: null } : r; });
            setLocalRoster(updated);
            onUpdateRoster && onUpdateRoster({ ...gameData, roster: updated });
            setSuspensionTarget(null);
          }}
          onConfirm={function(weeks, reason, type) {
            var isAbsence = type === 'absence';
            var updated = localRoster.map(function(r) {
              if (r.wrestler_id !== suspensionTarget.wrestler_id) return r;
              return { ...r, suspended: !isAbsence, absent: isAbsence, suspensionWeeksLeft: weeks, suspensionReason: reason || '', suspensionType: type || 'suspension' };
            });
            setLocalRoster(updated);
            onUpdateRoster && onUpdateRoster({ ...gameData, roster: updated });
            setSuspensionTarget(null);
          }}
        />
      )}

      {/* Release popup */}
      {releaseTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={() => setReleaseTarget(null)}>
          <div style={{ background: 'rgba(8,4,4,0.97)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '12px', padding: '28px 32px', width: '100%', maxWidth: '400px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <img src={getWrestlerRender(releaseTarget) || placeholder} alt="" style={{ width: '50px', height: '65px', objectFit: 'contain', objectPosition: 'top' }} />
              <div>
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#e74c3c' }}>RELEASE</div>
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.7rem', letterSpacing: '1px', color: '#fff' }}>{getWrestlerNameUpper(releaseTarget, 'display')}</div>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '6px' }}>SEVERANCE PAY (optional)</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>$</span>
                <input
                  type="number" min="0"
                  style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '8px 12px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none' }}
                  placeholder="0"
                  value={releaseAmount}
                  onChange={e => setReleaseAmount(e.target.value)}
                />
              </div>
              {releaseAmount > 0 && (
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#e67e22', marginTop: '5px' }}>
                  +${Number(releaseAmount).toLocaleString('fr-FR')} ajouté au budget
                </div>
              )}
            </div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.3)', marginBottom: '20px', lineHeight: '1.5' }}>
              Cette superstar sera retirée de ton roster. Cette action est irréversible.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setReleaseTarget(null)}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>CANCEL</button>
              <button onClick={() => {
                const severance = Number(releaseAmount) || 0;
                const newRoster = (gameData.roster || []).filter(r => r.wrestler_id !== releaseTarget.wrestler_id);
                const newBudget = (gameData.budget || 0) + severance;
                // Vacate any title held by the released wrestler
                const championsRaw = gameData.champions || {};
                const champMap = championsRaw.champions || {};
                const newChampMap = {};
                Object.entries(champMap).forEach(([key, champs]) => {
                  if (!champs || champs.length === 0) return;
                  const stillValid = champs.every(c => c && c.wrestler_id !== releaseTarget.wrestler_id);
                  if (stillValid) newChampMap[key] = champs;
                });
                const newChampions = { ...championsRaw, champions: newChampMap };
                setLocalRoster(newRoster);
                onUpdateRoster && onUpdateRoster({ ...gameData, roster: newRoster, budget: newBudget, champions: newChampions });
                setReleaseTarget(null);
              }}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.6)', color: '#e74c3c', padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>
                RELEASE
              </button>
            </div>
          </div>
        </div>
      )}

      {subTab === 'tagteams' && <TagTeamsTab gameData={gameData} roster={roster} brandColor={brandColor} onUpdateGameData={onUpdateRoster} />}
      {subTab === 'unavailable' && <UnavailableTab roster={localRoster} brandColor={brandColor} currentWeek={currentWeek} onInjure={(w) => setInjuryTarget(w)} onSuspend={(w) => setSuspensionTarget(w)} />}
      {editTarget && (
        <EditWrestlerPopup
          wrestler={editTarget}
          originalWrestler={roster?.find(w => w.wrestler_id === editTarget.wrestler_id)}
          onClose={() => setEditTarget(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <button className="btn-wwe" style={{ ...S.btn, fontSize: '0.5rem', padding: '5px 12px' }} onClick={onBack}>← BACK</button>
        <div style={{ ...S.anton, fontSize: '1.1rem', letterSpacing: '2px', color: '#fff' }}>ACTUAL ROSTER</div>
        <div style={{ ...S.mono, fontSize: '0.52rem', color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' }}>{filtered.length} superstars</div>
      </div>

      {/* Filters + search + view toggle */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <input style={{ ...S.input, flex: 1, minWidth: '160px' }} placeholder="SEARCH..." value={search} onChange={e => setSearch(e.target.value)}
          onFocus={e => e.target.style.borderColor = 'rgba(255,255,255,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'} />
        {/* Gender filter */}
        {['ALL', 'MALE', 'FEMALE'].map(g => (
          <button key={g} className="btn-wwe" onClick={() => setFilterGender(g)}
            style={{ ...S.btn, fontSize: '0.48rem', padding: '5px 10px', borderColor: filterGender === g ? brandColor : 'rgba(255,255,255,0.15)', color: filterGender === g ? '#fff' : 'rgba(255,255,255,0.35)', background: filterGender === g ? `${brandColor}22` : 'transparent' }}>
            {g}
          </button>
        ))}
        {/* Divider */}
        <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />
        {/* Sort */}
        <span style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>SORT</span>
        {[
          { key: 'name', asc: 'A → Z', desc: 'Z → A' },
          { key: 'class', asc: 'CLASS ↑', desc: 'CLASS ↓' },
          { key: 'xp', asc: 'XP ↑', desc: 'XP ↓' },
          { key: 'promo', asc: 'PROMO ↑', desc: 'PROMO ↓' },
        ].map(({ key, asc, desc }) => {
          const isActive = sortKey === key;
          return (
            <button key={key} className="btn-wwe"
              onClick={() => { if (isActive) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } }}
              style={{ ...S.btn, fontSize: '0.46rem', padding: '5px 10px', borderColor: isActive ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.15)', color: isActive ? '#fff' : 'rgba(255,255,255,0.35)', background: isActive ? 'rgba(255,255,255,0.07)' : 'transparent' }}>
              {isActive ? (sortDir === 'asc' ? asc : desc) : asc.split(' ')[0]}
            </button>
          );
        })}
        {/* View toggle */}
        <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', overflow: 'hidden', marginLeft: 'auto' }}>
          {[{ key: 'grid', icon: '⊞' }, { key: 'table', icon: '≡' }].map(({ key, icon }) => (
            <button key={key} className="view-btn" onClick={() => setView(key)}
              style={{ ...S.mono, fontSize: '0.7rem', background: view === key ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', color: view === key ? '#fff' : 'rgba(255,255,255,0.3)', padding: '5px 12px', cursor: 'pointer', transition: 'all 0.15s' }}>
              {icon}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {view === 'grid' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px', alignContent: 'flex-start' }}>
            {filtered.map((w, i) => (
              <UnifiedWrestlerCard
                key={`${w.wrestler_id}-${i}`}
                wrestler={w}
                brandColor={brandColor}
                rivalries={rivalries}
                champions={gameData && gameData.champions}
                roster={roster}
                currentWeek={currentWeek}
                onClick={() => setEditTarget(w)}
                onRelease={(w) => { handleReleaseRequest(w); }}
                onInjure={(w) => setInjuryTarget(w)}
                onSuspend={(w) => setSuspensionTarget(w)}
              />
            ))}
            {filtered.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', ...S.mono, fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)' }}>No superstars found.</div>}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.6fr 0.7fr 0.8fr 0.7fr 0.5fr 0.5fr 0.5fr 1.1fr', background: 'rgba(0,0,0,0.6)', flexShrink: 0 }}>
            {[['NAME','name'],['STATUS','status'],['GENDER',null],['PRIMARY CLASS','class'],['SECONDARY CLASS',null],['ROLE','role'],['XP','xp'],['PROMO',null],['',null]].map(([label, key]) => (
              <div key={label} style={key ? thStyle(key) : { ...thStyle(null), cursor: 'default', color: 'rgba(255,255,255,0.35)' }}
                onClick={() => { if (key) { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } } }}>
                {label}{key && sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.map((w, i) => (
              <div key={`${w.wrestler_id}-${i}`} className="row-hover"
                style={{ display: 'grid', gridTemplateColumns: '2fr 0.6fr 0.7fr 0.8fr 0.7fr 0.5fr 0.5fr 0.5fr 1.1fr', borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img src={getWrestlerRender(w) || placeholder} alt="" style={{ width: '24px', height: '30px', objectFit: 'contain', objectPosition: 'top' }} />
                  <div>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.6rem', color: '#fff' }}>{getWrestlerName(w, 'display')}</div>
                    {w.isCustom && <span style={{ ...S.mono, fontSize: '0.32rem', color: '#d4af37', background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '2px', padding: '0px 3px' }}>CUSTOM</span>}
                  </div>
                </div>
                <div style={{ padding: '8px 10px', ...S.mono, fontSize: '0.5rem', color: STATUS_COLORS[w.status] || '#fff', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>{w.status}</div>
                <div style={{ padding: '8px 10px', ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center' }}>{w.gender === 'F' ? 'Female' : 'Male'}</div>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center' }}><ClassIcon cls={w.fight_class} /></div>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center' }}>{w.fight_class_secondary ? <ClassIcon cls={w.fight_class_secondary} /> : <span style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.2)' }}>—</span>}</div>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center' }}><RoleIcon heel={w.heel} /></div>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center' }}><XpIcon xp={w.starting_xp || 0} /></div>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center' }}><PromoIcon level={w.promo_skill || 1} /></div>
                <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button className="btn-wwe" style={{ ...S.btn, fontSize: '0.42rem', padding: '3px 8px' }} onClick={() => setEditTarget(w)}>EDIT</button>
                  <button className="btn-wwe" style={{ ...S.btn, fontSize: '0.42rem', padding: '3px 6px', borderColor: 'rgba(231,76,60,0.4)', color: 'rgba(231,76,60,0.7)' }} onClick={() => { handleReleaseRequest(w); }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Roster Hub (Concept B — compact cards with live stats) ──────────
const RosterHub = ({ onSelect, gameData }) => {
  var roster = (gameData && gameData.roster) || [];
  var rivalries = (gameData && gameData.rivalries) || [];
  var championsRaw = (gameData && gameData.champions) || {};
  var champSlots = championsRaw.slots || [];
  var champMap = championsRaw.champions || {};
  var tagTeams = (gameData && gameData.tagTeams) || [];

  // Roster stats
  var activeCount = roster.filter(function(w) { return w && w.status === 'active'; }).length;
  var injuredCount = roster.filter(function(w) { return w && (w.injured || w.suspended || w.absent); }).length;
  var heelCount = roster.filter(function(w) { return w && w.status === 'active' && w.heel; }).length;
  var faceCount = roster.filter(function(w) { return w && w.status === 'active' && !w.heel; }).length;

  // Rivalry stats
  var activeRivalries = rivalries.filter(function(r) { return r && !r.ended; }).length;
  var bloodFeuds = rivalries.filter(function(r) { return r && !r.ended && r.level >= 4; }).length;

  // Championship stats
  var totalTitles = champSlots.length;
  var vacantCount = champSlots.filter(function(s) {
    var champs = champMap[s.key] || [];
    return !champs || champs.length === 0 || champs.every(function(c) { return !c; });
  }).length;

  // Recruitment stats
  var availableRecruits = (gameData && gameData.recruitPool) ? (gameData.recruitPool.length) : null;

  var hubCss = `
    @import url('https://fonts.googleapis.com/css2?family=Anton&family=Monda:wght@400;700&display=swap');
    * { box-sizing: border-box; }
    .hub-card { transition: all 0.22s; cursor: pointer; }
    .hub-card:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(0,0,0,0.6); }
    .hub-card:hover .hub-img { filter: brightness(0.85) !important; }
    .hub-card:hover .hub-overlay { opacity: 0.7 !important; }
    .hub-border-gradient { position: absolute; inset: 0; border-radius: 12px; padding: 1px; background: linear-gradient(90deg, #c0392b, #2980b9); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; z-index: 10; }
  `;

  var CARD_DATA = [
    {
      key: 'actual',
      label: 'ROSTER',
      desc: 'Your locker room. Every face, every heel.',
      color: '#6c3483',
      asset: process.env.PUBLIC_URL + '/roster/roster_roster.png',
      stats: [
        { val: activeCount, lbl: 'ACTIVE' },
        injuredCount > 0 ? { val: injuredCount, lbl: 'OUT', color: '#e74c3c' } : null,
        { val: heelCount + 'H / ' + faceCount + 'F', lbl: 'ALIGNMENT', small: true },
      ].filter(Boolean),
    },
    {
      key: 'rivalries',
      label: 'RIVALRIES',
      desc: 'Feuds that sell tickets. Storylines that keep them watching.',
      color: '#c0392b',
      asset: process.env.PUBLIC_URL + '/roster/roster_rivalries.png',
      stats: [
        { val: activeRivalries, lbl: 'ACTIVE' },
        bloodFeuds > 0 ? { val: bloodFeuds, lbl: 'BLOOD FEUD', color: '#d4af37' } : { val: '—', lbl: 'BLOOD FEUD' },
      ],
    },
    {
      key: 'championships',
      label: 'CHAMPIONSHIPS',
      desc: 'Gold defines greatness. Who reigns, who hungers.',
      color: '#b7950b',
      asset: process.env.PUBLIC_URL + '/roster/roster_championships.png',
      stats: [
        { val: totalTitles, lbl: 'TITLES' },
        vacantCount > 0 ? { val: vacantCount, lbl: 'VACANT', color: '#e74c3c' } : { val: '—', lbl: 'VACANT' },
        tagTeams.length > 0 ? { val: tagTeams.length, lbl: 'TAG TEAMS' } : null,
      ].filter(Boolean),
    },
    {
      key: 'recruitment',
      label: 'RECRUITMENT',
      desc: 'Build the future. Sign the legend. Lock them in.',
      color: '#1a8a4a',
      asset: process.env.PUBLIC_URL + '/roster/roster_recruitment.png',
      stats: [
        availableRecruits !== null ? { val: availableRecruits, lbl: 'AVAILABLE' } : { val: '—', lbl: 'AVAILABLE' },
      ],
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
      <style>{hubCss}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
        <div style={{ fontFamily: "'Anton',sans-serif", letterSpacing: '3px', color: '#fff', fontSize: '1.2rem' }}>ROSTER MANAGEMENT</div>
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '1px' }}>WEEK {(gameData && gameData.week) || 1}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', flex: 1 }}>
        {CARD_DATA.map(function(card) {
          var isRivalries = card.key === 'rivalries';
          return (
            <div key={card.key} className="hub-card" onClick={function() { onSelect(card.key); }}
              style={{ '--card-color': card.color + '99', position: 'relative', borderRadius: '12px', overflow: 'hidden', border: isRivalries ? 'none' : '1px solid ' + card.color + '55', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '200px' }}>

              {/* Rivalries : border dégradé rouge → bleu */}
              {isRivalries && <div className="hub-border-gradient" />}

              {/* Background asset — assombri de 5% */}
              <img className="hub-img" src={card.asset} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, pointerEvents: 'none', filter: 'brightness(0.8)', transition: 'filter 0.22s' }}
                onError={function(e) { e.target.style.display='none'; }} />

              {/* Gradient overlay */}
              <div className="hub-overlay" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.88) 100%)', zIndex: 1, pointerEvents: 'none', transition: 'opacity 0.22s' }} />

              {/* Ligne colorée bas */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: isRivalries ? 'linear-gradient(90deg, #c0392b, #2980b9)' : card.color, zIndex: 3, opacity: 0.9 }} />

              {/* Encart nom + description */}
              <div style={{ position: 'relative', zIndex: 2, margin: '12px', background: 'rgba(0,0,0,0.62)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '7px', padding: '10px 14px', backdropFilter: 'blur(8px)', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.78rem', letterSpacing: '2.5px', color: '#fff', lineHeight: 1, marginBottom: '4px' }}>{card.label}</div>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{card.desc}</div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── WIP placeholder ─────────────────────────────────────────────────
const WIPPage = ({ label, onBack }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', height: '100%' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <button className="btn-wwe" style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', letterSpacing: '1.5px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.6)', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer' }} onClick={onBack}>← BACK</button>
      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.1rem', letterSpacing: '2px', color: '#fff' }}>{label}</div>
    </div>
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '2rem', letterSpacing: '4px', color: 'rgba(255,255,255,0.08)' }}>{label}</div>
      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '2px' }}>TO BE DEVELOPED</div>
    </div>
  </div>
);

// ── MAIN EXPORT ─────────────────────────────────────────────────────

// ── Championships Tab ───────────────────────────────────────────────
const ChampionshipsTab = ({ gameData, onBack, onUpdateGameData }) => {
  const championsRaw = gameData?.champions || {};
  const champSlots = championsRaw?.slots || [];
  const champMap = championsRaw?.champions || {};
  const brand = gameData?.brand || '';
  const brandColor = BRAND_COLORS[brand] || '#d4af37';
  const currentWeek = gameData?.week || 1;
  const currentSeason = gameData?.season || 1;

  const [pickerSlot, setPickerSlot] = useState(null);
  const [localChampions, setLocalChampions] = useState(champMap);

  // Init history - if no history yet, create initial reigns from current champions
  const initHistory = () => {
    var existing = gameData?.titleHistory || [];
    if (existing.length > 0) return existing;
    var initial = [];
    Object.keys(champMap).forEach(function(key) {
      var champs = champMap[key];
      if (champs && champs.length > 0) {
        initial.push({
          titleKey: key,
          champions: champs.map(function(c) { return getWrestlerName(c, 'display'); }),
          startWeek: currentWeek,
          startSeason: currentSeason,
          endWeek: null,
          endSeason: null,
        });
      }
    });
    return initial;
  };
  const [titleHistory, setTitleHistory] = useState(initHistory);
  const [editingReign, setEditingReign] = useState(null); // for manual edit
  const [selectedTitle, setSelectedTitle] = useState(null); // to show history for

  const saveAll = (newChampions, newHistory) => {
    onUpdateGameData && onUpdateGameData({ ...gameData, champions: { ...championsRaw, champions: newChampions }, titleHistory: newHistory });
  };

  const handleVacant = (key) => {
    // Close current reign
    const oldChamps = localChampions[key];
    let newHistory = [...titleHistory];
    if (oldChamps && oldChamps.length > 0) {
      // Find open reign for this key
      const openIdx = newHistory.findIndex(r => r.titleKey === key && !r.endWeek);
      if (openIdx !== -1) {
        newHistory[openIdx] = { ...newHistory[openIdx], endWeek: currentWeek, season: currentSeason };
      }
    }
    const updated = { ...localChampions };
    delete updated[key];
    setLocalChampions(updated);
    setTitleHistory(newHistory);
    saveAll(updated, newHistory);
  };

  const handleSave = (key, picks) => {
    // Close current reign if there's a champion
    const oldChamps = localChampions[key];
    let newHistory = [...titleHistory];
    if (oldChamps && oldChamps.length > 0 && picks && picks.length > 0) {
      const openIdx = newHistory.findIndex(r => r.titleKey === key && !r.endWeek);
      if (openIdx !== -1) {
        newHistory[openIdx] = { ...newHistory[openIdx], endWeek: currentWeek };
      }
    }
    // Open new reign
    if (picks && picks.length > 0) {
      newHistory.push({
        titleKey: key,
        champions: picks.map(p => getWrestlerName(p, 'display')),
        startWeek: currentWeek,
        startSeason: currentSeason,
        endWeek: null,
        endSeason: null,
      });
    }
    const updated = { ...localChampions, [key]: picks };
    setLocalChampions(updated);
    setTitleHistory(newHistory);
    setPickerSlot(null);
    saveAll(updated, newHistory);
  };

  if (champSlots.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.5rem', color: 'rgba(255,255,255,0.1)', letterSpacing: '4px' }}>NO TITLES SET</div>
        <button onClick={onBack} style={{ ...S.btn, fontSize: '0.55rem' }}>← BACK</button>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexShrink: 0 }}>
        <button onClick={onBack} style={{ ...S.btn, fontSize: '0.5rem', padding: '6px 14px' }}>← BACK</button>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', letterSpacing: '3px', color: '#fff' }}>CHAMPIONSHIPS</div>
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>{brand.toUpperCase()}</div>
      </div>

      {/* Title cards — sorted: male singles, tag teams, female singles */}
      {(() => {
        var maleSlots = champSlots.filter(s => !s.isTagTeam && s.key && s.key.startsWith('male'));
        var tagSlots  = champSlots.filter(s => s.isTagTeam);
        var femaleSlots = champSlots.filter(s => !s.isTagTeam && s.key && s.key.startsWith('female'));
        var otherSlots = champSlots.filter(s => !maleSlots.includes(s) && !tagSlots.includes(s) && !femaleSlots.includes(s));
        var ordered = [...maleSlots, ...tagSlots, ...femaleSlots, ...otherSlots];

        return (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center', alignContent: 'flex-start' }}>
            {ordered.map(slot => {
              const champs = localChampions[slot.key];
              const hasChamp = champs && champs.length > 0 && champs[0];
              const titleRender = (slot.titleOverride && (slot.titleOverride.render || getTitleRender(slot.titleOverride.id))) || placeholderTitle;
              const titleName = (slot.titleOverride && slot.titleOverride.name) || slot.label || slot.key;
              const cardWidth = slot.isTagTeam ? '340px' : '180px';

              return (
                <div key={slot.key}
                  style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid ' + (hasChamp ? 'rgba(212,175,55,0.35)' : 'rgba(255,255,255,0.08)'), borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', width: cardWidth, flexShrink: 0 }}>

                  {hasChamp && <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at top, rgba(212,175,55,0.08) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 }} />}

              {/* Belt */}
              <div style={{ padding: '14px 10px 6px', display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                <img src={titleRender} alt={titleName}
                  style={{ width: slot.isTagTeam ? '70%' : '90%', maxWidth: '180px', height: '60px', objectFit: 'contain', filter: 'drop-shadow(0 4px 12px rgba(212,175,55,' + (hasChamp ? '0.5' : '0.2') + '))' }}
                  onError={e => { e.target.src = placeholderTitle; }} />
              </div>

              {/* Champion render(s) */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                {hasChamp ? (
                  slot.isTagTeam ? (
                    <div style={{ position: 'relative', height: '300px' }}>
                      <img src={getWrestlerRender(champs[0]) || placeholder} alt=""
                        style={{ height: '290px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 12px rgba(212,175,55,0.3))', position: 'absolute', left: '-15%', bottom: 0, zIndex: 1 }} />
                      <img src={getWrestlerRender(champs[1]) || placeholder} alt=""
                        style={{ height: '290px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 12px rgba(212,175,55,0.4))', position: 'absolute', right: '-15%', bottom: 0, zIndex: 2 }} />
                    </div>
                  ) : (
                    <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden' }}>
                      <img src={getWrestlerRender(champs[0]) || placeholder} alt=""
                        style={{ height: '322px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 16px rgba(212,175,55,0.4))', marginBottom: '-8px' }} />
                    </div>
                  )
                ) : (
                  <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.7rem', color: 'rgba(255,255,255,0.15)', letterSpacing: '2px' }}>VACANT</div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '8px 10px 10px', background: 'rgba(0,0,0,0.8)', borderTop: '1px solid ' + (hasChamp ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.06)'), position: 'relative', zIndex: 1 }}>
                {hasChamp && (
                  <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1px', color: '#d4af37', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                    {slot.isTagTeam
                      ? (champs[0] && champs[1] ? getWrestlerNameUpper(champs[0], 'display') + ' & ' + getWrestlerNameUpper(champs[1], 'display') : champs[0] && getWrestlerNameUpper(champs[0], 'display'))
                      : getWrestlerNameUpper(champs[0], 'display')}
                  </div>
                )}
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', marginBottom: '8px', letterSpacing: '0.5px' }}>{titleName}</div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => setPickerSlot(slot)}
                    style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', letterSpacing: '1px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.35)', color: '#d4af37', padding: '6px 8px', borderRadius: '4px', cursor: 'pointer' }}>
                    {hasChamp ? '✏ EDIT' : '+ SET'}
                  </button>
                  {hasChamp && (
                    <button
                      onClick={() => handleVacant(slot.key)}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', letterSpacing: '1px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.35)', color: 'rgba(231,76,60,0.7)', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                      VACANT
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
          </div>
        );
      })()}

      {/* Title History */}
      <div style={{ marginTop: '24px', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '3px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>TITLE HISTORY</div>
          {/* Title selector */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => setSelectedTitle(null)}
              style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1px', background: 'transparent', border: '1px solid ' + (!selectedTitle ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.15)'), color: !selectedTitle ? '#d4af37' : 'rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer' }}>ALL</button>
            {champSlots.map(s => (
              <button key={s.key} onClick={() => setSelectedTitle(selectedTitle === s.key ? null : s.key)}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1px', background: 'transparent', border: '1px solid ' + (selectedTitle === s.key ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.15)'), color: selectedTitle === s.key ? '#d4af37' : 'rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {(s.titleOverride && s.titleOverride.name) || s.label}
              </button>
            ))}
          </div>
          <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.5fr', background: 'rgba(0,0,0,0.5)', padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              {['CHAMPION', 'TITLE', 'START', 'END', ''].map(h => (
                <div key={h} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px' }}>{h}</div>
              ))}
            </div>
            {/* Rows - newest first */}
            {[...titleHistory].reverse().filter(r => !selectedTitle || r.titleKey === selectedTitle).map((reign, i) => {
              const slot = champSlots.find(s => s.key === reign.titleKey);
              const titleName = slot && slot.titleOverride && slot.titleOverride.name || reign.titleKey;
              const duration = reign.endWeek ? reign.endWeek - reign.startWeek : currentWeek - reign.startWeek;
              const isCurrent = !reign.endWeek;
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.5fr', padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: isCurrent ? 'rgba(212,175,55,0.04)' : i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.55rem', color: isCurrent ? '#d4af37' : '#fff', letterSpacing: '1px' }}>
                      {reign.champions ? reign.champions.join(' & ') : '—'}
                      {isCurrent && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#d4af37', marginLeft: '6px', letterSpacing: '1px' }}>● CURRENT</span>}
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titleName}</div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.5)' }}>S{reign.startSeason || 1} W{reign.startWeek}</div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: isCurrent ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.4)' }}>
                    {isCurrent ? duration + ' wk(s)' : 'S' + (reign.endSeason || 1) + ' W' + reign.endWeek + ' (' + duration + ' wk)'}
                  </div>
                  <div>
                    <button onClick={() => setEditingReign({ ...reign, _idx: titleHistory.length - 1 - i })}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '2px 7px', borderRadius: '3px', cursor: 'pointer' }}>✏</button>
                  </div>
                </div>
              );
            })}
            {titleHistory.filter(r => !selectedTitle || r.titleKey === selectedTitle).length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.2)' }}>No title history yet.</div>
            )}
          </div>
      </div>

      {/* Edit reign popup */}
      {editingReign && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          onClick={() => setEditingReign(null)}>
          <div style={{ background: 'rgba(8,6,0,0.97)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '10px', padding: '24px', width: '100%', maxWidth: '400px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#d4af37', marginBottom: '16px' }}>EDIT REIGN</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', letterSpacing: '1px' }}>CHAMPION(S)</div>
                <input style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '7px 10px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '100%' }}
                  value={editingReign.champions ? editingReign.champions.join(' & ') : ''}
                  onChange={e => setEditingReign(p => ({ ...p, champions: e.target.value.split('&').map(s => s.trim()) }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', letterSpacing: '1px' }}>START WEEK</div>
                  <input type="number" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '7px 10px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '100%' }}
                    value={editingReign.startWeek || ''} onChange={e => setEditingReign(p => ({ ...p, startWeek: Number(e.target.value) }))} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', letterSpacing: '1px' }}>END WEEK</div>
                  <input type="number" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '7px 10px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '100%' }}
                    value={editingReign.endWeek || ''} onChange={e => setEditingReign(p => ({ ...p, endWeek: e.target.value ? Number(e.target.value) : null }))}
                    placeholder="Leave empty if current" />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setEditingReign(null)}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer' }}>CANCEL</button>
              <button onClick={() => {
                const newHistory = [...titleHistory];
                const realIdx = titleHistory.length - 1 - editingReign._idx;
                newHistory[realIdx] = { ...editingReign };
                delete newHistory[realIdx]._idx;
                setTitleHistory(newHistory);
                saveAll(localChampions, newHistory);
                setEditingReign(null);
              }}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(212,175,55,0.5)', color: '#d4af37', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer' }}>SAVE</button>
            </div>
          </div>
        </div>
      )}

      {/* Champion picker popup */}
      {pickerSlot && (
        <ChampionPickerPopup
          slot={pickerSlot}
          currentChampions={localChampions[pickerSlot.key]}
          roster={gameData?.roster || []}
          onClose={() => setPickerSlot(null)}
          onSave={(picks) => { handleSave(pickerSlot.key, picks); setPickerSlot(null); }}
          brandColor={brandColor}
        />
      )}
    </div>
  );
};

// ── Champion Picker Popup — uses SuperstarPicker ─────────────────────
const ChampionPickerPopup = ({ slot, currentChampions, roster, onClose, onSave, brandColor }) => {
  const [picked1, setPicked1] = useState(currentChampions?.[0] || null);
  const [picked2, setPicked2] = useState(currentChampions?.[1] || null);

  const defaultGender = slot.gender === 'female' ? 'FEMALE' : slot.gender === 'male' ? 'MALE' : 'ALL';
  const selectedIds = [picked1, picked2].filter(Boolean).map(p => p.wrestler_id);
  const canConfirm = picked1 && (!slot.isTagTeam || picked2);

  const handleSelect = (w) => {
    if (!slot.isTagTeam) {
      setPicked1(p => p?.wrestler_id === w.wrestler_id ? null : w);
      return;
    }
    // Tag : toggle
    if (picked1?.wrestler_id === w.wrestler_id) { setPicked1(null); return; }
    if (picked2?.wrestler_id === w.wrestler_id) { setPicked2(null); return; }
    if (!picked1) { setPicked1(w); return; }
    if (!picked2) { setPicked2(w); return; }
    // Les deux sont pris — remplace le premier
    setPicked1(w); setPicked2(null);
  };

  const handleConfirm = () => {
    if (slot.isTagTeam) onSave(picked1 && picked2 ? [picked1, picked2] : null);
    else onSave(picked1 ? [picked1] : null);
    onClose();
  };

  return (
    <SuperstarPicker
      roster={roster}
      alreadyPicked={[]}
      onSelect={handleSelect}
      onClose={onClose}
      filterActive={true}
      autoClose={false}
      showSynergy={false}
      defaultGender={defaultGender}
      selectedIds={selectedIds}
      showConfirm={true}
      onConfirm={handleConfirm}
      confirmDisabled={!canConfirm}
      title={slot.isTagTeam ? `SELECT PARTNERS (${selectedIds.length}/2)` : 'SELECT CHAMPION'}
      rivalries={[]}
    />
  );
};



// ── Rivalries Tab ────────────────────────────────────────────────────
const RIVALRY_LEVEL_COLORS = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#d4af37' };
const RIVALRY_LEVEL_LABELS = { 1: 'Niv.1 - Début', 2: 'Niv.2 - Intense', 3: 'Niv.3 - Chauffé', 4: 'Niv.4 - Blood Feud' };

// ── Rivalry Detail Modal ─────────────────────────────────────────────
const IMPACT_LABELS = { 'create': '⚡ CREATED', 'heatup': '🔥 HEAT UP', 'cooldown': '❄ COOL DOWN', 'end': '✓ ENDED', 'maintain': '— MAINTAIN' };
const IMPACT_COLORS = { 'create': '#f1c40f', 'heatup': '#e74c3c', 'cooldown': '#3498db', 'end': '#888', 'maintain': 'rgba(255,255,255,0.4)' };

const RivalryDetailModal = ({ rivalry, roster, brandColor, onClose, champions }) => {
  if (!rivalry) return null;
  var lvlColor = RIVALRY_LEVEL_COLORS[rivalry.level] || '#fff';
  var events = rivalry.events || [];
  var isEnded = rivalry.ended;

  var getSideName = function(ids) {
    return ids.map(function(id, idx) {
      var w = roster.find(function(w) { return String(w.wrestler_id) === String(id); });
      if (w) return getWrestlerName(w, 'display');
      // Fallback to snapshot
      var snapshots = (rivalry.side1 || []).some(function(sid) { return String(sid) === String(id); }) ? (rivalry.side1Snapshot || []) : (rivalry.side2Snapshot || []);
      var snap = snapshots.find(function(s) { return String(s.wrestler_id) === String(id); });
      return snap ? snap.name : '?'; // snapshot déjà calculé
    }).filter(Boolean).join(' & ');
  };

  var getSideRender = function(ids, sideKey) {
    return ids.map(function(id) {
      var w = roster.find(function(w) { return String(w.wrestler_id) === String(id); });
      if (w) return w;
      // Fallback to snapshot - use snapshot data as wrestler-like object
      var snapshots = sideKey === 'side1' ? (rivalry.side1Snapshot || []) : (rivalry.side2Snapshot || []);
      var snap = snapshots.find(function(s) { return String(s.wrestler_id) === String(id); });
      if (snap) {
        return { ...snap, customRender: snap.render, _isSnapshot: true };
      }
      return null;
    }).filter(Boolean);
  };

  var side1Members = getSideRender(rivalry.side1 || [], 'side1');
  var side2Members = getSideRender(rivalry.side2 || [], 'side2');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(8,8,8,0.98)', border: '1px solid ' + lvlColor + '44', borderRadius: '12px', width: 'min(95vw, 860px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={function(e) { e.stopPropagation(); }}>

        {/* Top bar */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + lvlColor + ', transparent)' }} />

        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: lvlColor, letterSpacing: '2px', marginBottom: '2px' }}>
                {isEnded ? 'ENDED — W' + rivalry.startWeek + ' → W' + rivalry.endWeek : 'ACTIVE — SINCE W' + rivalry.startWeek}
              </div>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '2px', color: '#fff' }}>
                {getSideName(rivalry.side1 || [])} <span style={{ color: '#e74c3c' }}>VS</span> {getSideName(rivalry.side2 || [])}
              </div>
            </div>
            {!isEnded && (
              <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                {[1,2,3,4].map(function(l) { return (
                  <div key={l} style={{ width: '20px', height: '4px', borderRadius: '2px', background: l <= rivalry.level ? lvlColor : 'rgba(255,255,255,0.1)' }} />
                ); })}
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: lvlColor, marginLeft: '6px' }}>{RIVALRY_LEVEL_LABELS[rivalry.level]}</div>
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

          {/* Left — renders */}
          <div style={{ width: '220px', flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', background: 'rgba(0,0,0,0.3)' }}>
            {/* Side 1 */}
            <div style={{ display: 'flex', justifyContent: 'center', position: 'relative', height: '130px', width: '100%' }}>
              {side1Members.map(function(w, i) { return (
                <img key={i} src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
                  style={{ position: 'absolute', bottom: 0, height: '130px', objectFit: 'contain', objectPosition: 'top', left: side1Members.length > 1 ? (i === 0 ? '0%' : '30%') : '50%', transform: side1Members.length === 1 ? 'translateX(-50%)' : 'none', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.9))', zIndex: i + 1 }} />
              ); })}
            </div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>{getSideName(rivalry.side1 || []).toUpperCase()}</div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', color: '#e74c3c', textShadow: '0 0 16px rgba(231,76,60,0.8)' }}>VS</div>
            {/* Side 2 */}
            <div style={{ display: 'flex', justifyContent: 'center', position: 'relative', height: '130px', width: '100%' }}>
              {side2Members.map(function(w, i) { return (
                <img key={i} src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
                  style={{ position: 'absolute', bottom: 0, height: '130px', objectFit: 'contain', objectPosition: 'top', left: side2Members.length > 1 ? (i === 0 ? '0%' : '30%') : '50%', transform: side2Members.length === 1 ? 'translateX(-50%)' : 'none', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.9))', zIndex: i + 1 }} />
              ); })}
            </div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>{getSideName(rivalry.side2 || []).toUpperCase()}</div>

            {/* Final winner if ended */}
            {isEnded && rivalry.finalWinner && (
              <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px', textAlign: 'center', width: '100%' }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.4)', marginBottom: '2px' }}>FINAL WINNER</div>
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.55rem', color: '#d4af37' }}>👑 {rivalry.finalWinner.name ? rivalry.finalWinner.name.toUpperCase() : getWrestlerNameUpper(rivalry.finalWinner, 'display')}</div>
              </div>
            )}

            {/* Storyline preset + narrative */}
            {(function() {
              var st = rivalry.storylineId ? STORYLINES.find(function(s) { return s.id === rivalry.storylineId; }) : null;
              if (!st) return null;
              var phase = st.phases.find(function(p) { return p.level === (rivalry.level || 1); });
              var storyText = null;
              if (phase && phase.variants && phase.variants.length > 0) {
                var championsData = champions;
                var vars = buildVars(rivalry, roster, championsData);
                var idx = (rivalry.storylineVariantByLevel && rivalry.storylineVariantByLevel[rivalry.level]) || 0;
                storyText = resolveText(phase.variants[idx % phase.variants.length], vars);
              }
              return (
                <div style={{ marginTop: '8px', padding: '10px 14px', background: 'rgba(155,89,182,0.06)', border: '1px solid rgba(155,89,182,0.2)', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.7rem' }}>{st.icon}</span>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.55rem', letterSpacing: '2px', color: '#9b59b6' }}>{st.name.toUpperCase()}</div>
                    {phase && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(155,89,182,0.6)', letterSpacing: '1px' }}>· {phase.name.toUpperCase()}</div>}
                  </div>
                  {storyText && (
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.65)', textAlign: 'center', fontStyle: 'italic', lineHeight: '1.6' }}>"{storyText}"</div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Right — timeline */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', marginBottom: '12px' }}>TIMELINE — {events.length} EVENTS</div>

            {events.length === 0 && (
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '40px 0' }}>No events recorded yet</div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {events.map(function(ev, i) {
                var impactColor = IMPACT_COLORS[ev.impact] || 'rgba(255,255,255,0.4)';
                var isMatch = ev.type === 'match';
                var isPromo = ev.type === 'promo';
                var isCreate = ev.type === 'create';
                var side1Name = getSideName(rivalry.side1 || []);
                var side2Name = getSideName(rivalry.side2 || []);

                return (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid ' + impactColor + '33', borderRadius: '8px', overflow: 'hidden' }}>
                    {/* Event header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: impactColor + '08' }}>
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', flexShrink: 0 }}>W{ev.week}</div>
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: impactColor, letterSpacing: '1px', flexShrink: 0 }}>{IMPACT_LABELS[ev.impact] || ev.impact}</div>
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.05)', borderRadius: '2px', padding: '1px 5px' }}>
                        {isCreate ? '⚡ CREATE' : isMatch ? '🤼 MATCH' : '🎤 PROMO'}
                      </div>
                      {ev.rating > 0 && (
                        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#d4af37', marginLeft: 'auto' }}>{'★'.repeat(Math.floor(ev.rating))}{ev.rating % 1 === 0.5 ? '½' : ''}</div>
                      )}
                    </div>

                    {/* Event body */}
                    <div style={{ padding: '8px 12px' }}>
                      {isMatch && (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {ev.participants.map(function(p, pi) { return (
                              <React.Fragment key={pi}>
                                {pi > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#e74c3c' }}>vs</span>}
                                <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: (ev.winner && ev.winner.wrestler_id === p.wrestler_id) ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: (ev.winner && ev.winner.wrestler_id === p.wrestler_id) ? 'bold' : 'normal' }}>
                                  {ev.winner && ev.winner.wrestler_id === p.wrestler_id && '👑 '}{p.name || getWrestlerName(p, 'display')}
                                </span>
                              </React.Fragment>
                            ); })}
                            {ev.noContest && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: '#e74c3c' }}>— NO CONTEST</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                            {ev.matchType && ev.matchType !== 'normal' && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: '2px' }}>{ev.matchType}</span>}
                            {ev.matchRule && ev.matchRule !== '1v1' && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: '2px' }}>{ev.matchRule}</span>}
                            {ev.titleMatch && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: '#d4af37', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', padding: '1px 5px', borderRadius: '2px' }}>🏆 {ev.titleChange ? 'TITLE CHANGE' : 'TITLE MATCH'}</span>}
                          </div>
                        </div>
                      )}
                      {(isPromo || isCreate) && (
                        <div>
                          {ev.promoAction && (
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#9b59b6', marginBottom: '4px' }}>{ev.promoAction.replace(/-/g, ' ').toUpperCase()}</div>
                          )}
                          {ev.callers && ev.callers.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#fff' }}>{ev.callers.map(function(c) { return c.name || getWrestlerName(c, 'display') || '?'; }).join(' & ')}</span>
                              {ev.targets && ev.targets.length > 0 && (
                                <React.Fragment>
                                  <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', color: '#e74c3c' }}>→</span>
                                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.6)' }}>{ev.targets.map(function(t) { return t.name || getWrestlerName(t, 'display') || '?'; }).join(' & ')}</span>
                                </React.Fragment>
                              )}
                            </div>
                          )}
                          {(!ev.callers || ev.callers.length === 0) && ev.participants.length > 0 && (
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#fff' }}>{ev.participants.map(function(p) { return p.name || getWrestlerName(p, 'display') || '?'; }).join(', ')}</div>
                          )}
                        </div>
                      )}
                      {ev.notes && (
                        <div style={{ marginTop: '4px', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>{ev.notes}</div>
                      )}

                      {/* Storyline narrative — only on meaningful events, not maintain */}
                      {(function() {
                        if (!ev.storylineId || ev.storylineVariantIdx == null) return null;
                        if (ev.impact === 'maintain') return null;
                        var st = STORYLINES.find(function(s) { return s.id === ev.storylineId; });
                        if (!st) return null;
                        var phase = st.phases.find(function(p) { return p.level === ev.rivalryLevel; });
                        if (!phase || !phase.variants || phase.variants.length === 0) return null;
                        var championsData = champions;
                var vars = buildVars(rivalry, roster, championsData);
                        var text = resolveText(phase.variants[ev.storylineVariantIdx % phase.variants.length], vars);
                        return (
                          <div style={{ marginTop: '8px', padding: '8px 10px', background: 'rgba(155,89,182,0.06)', border: '1px solid rgba(155,89,182,0.15)', borderRadius: '4px', borderLeft: '3px solid #9b59b6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                              <span style={{ fontSize: '0.55rem' }}>{st.icon}</span>
                              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(155,89,182,0.8)', letterSpacing: '1px', textTransform: 'uppercase' }}>{phase.name}</div>
                            </div>
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.65)', fontStyle: 'italic', lineHeight: 1.5 }}>"{text}"</div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RivalryCard = ({ rivalry, roster, onEdit, onEnd, brandColor, onView, champions }) => {
  const isTeam = rivalry.type === 'team';
  const lvlColor = RIVALRY_LEVEL_COLORS[rivalry.level] || '#fff';

  const renderSide = (ids, label) => {
    const members = ids.map(id => roster.find(w => w.wrestler_id === id || w.wrestler_id === Number(id))).filter(Boolean);
    const isTeamSide = members.length > 1;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flex: 1 }}>
        {members.length > 0 ? (
          isTeamSide ? (
            // Superposed like dashboard tag champions
            <div style={{ position: 'relative', height: '180px', width: '100%' }}>
              <img src={getWrestlerRender(members[0]) || placeholder} alt={getWrestlerName(members[0], 'display')}
                style={{ position: 'absolute', bottom: 0, left: '-8%', height: '170px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.9))', zIndex: 1 }} />
              <img src={getWrestlerRender(members[1]) || placeholder} alt={getWrestlerName(members[1], 'display')}
                style={{ position: 'absolute', bottom: 0, right: '-8%', height: '170px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.9))', zIndex: 2 }} />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '180px' }}>
              <img src={getWrestlerRender(members[0]) || placeholder} alt={getWrestlerName(members[0], 'display')}
                style={{ height: '180px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.8))' }} />
            </div>
          )
        ) : (
          <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '80px' }}>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.2)' }}>?</div>
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          {members.map((w, i) => (
            <div key={i} style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', letterSpacing: '1px', color: '#fff', whiteSpace: 'nowrap' }}>{getWrestlerNameUpper(w, 'display')}</div>
          ))}
          {members.length === 0 && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.3)' }}>{label}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: 'rgba(8,8,8,0.9)', border: '1px solid ' + lvlColor + '44', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
      {/* Level bar */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + lvlColor + ', transparent)' }} />
      {(function() {
        var st = rivalry.storylineId ? STORYLINES.find(function(s) { return s.id === rivalry.storylineId; }) : null;
        if (!st) {
          // fallback to legacy string storyline if present
          return rivalry.storyline ? (
            <div style={{ padding: '8px 14px 0', fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.5)', lineHeight: '1.5', fontStyle: 'italic', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              "{rivalry.storyline}"
            </div>
          ) : null;
        }
        var phase = st.phases.find(function(p) { return p.level === (rivalry.level || 1); });
        var storyText = null;
        if (phase && phase.variants && phase.variants.length > 0) {
          var championsData = champions;
                var vars = buildVars(rivalry, roster, championsData);
          var idx = (rivalry.storylineVariantByLevel && rivalry.storylineVariantByLevel[rivalry.level]) || 0;
          storyText = resolveText(phase.variants[idx % phase.variants.length], vars);
        }
        return (
          <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(155,89,182,0.12)', background: 'rgba(155,89,182,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: storyText ? '4px' : 0 }}>
              <span style={{ fontSize: '0.65rem' }}>{st.icon}</span>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', letterSpacing: '1.5px', color: '#9b59b6' }}>{st.name.toUpperCase()}</div>
              {phase && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: 'rgba(155,89,182,0.55)', letterSpacing: '1px' }}>· {phase.name.toUpperCase()}</div>}
            </div>
            {storyText && (
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', lineHeight: 1.5 }}>"{storyText}"</div>
            )}
          </div>
        );
      })()}
      <div style={{ padding: '10px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '3px' }}>
            {[1,2,3,4].map(l => (
              <div key={l} style={{ width: '18px', height: '4px', borderRadius: '2px', background: l <= rivalry.level ? lvlColor : 'rgba(255,255,255,0.1)' }} />
            ))}
          </div>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: lvlColor, letterSpacing: '1px' }}>{RIVALRY_LEVEL_LABELS[rivalry.level]}</div>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>W{rivalry.startWeek} · {rivalry.weeks} wk{rivalry.weeks > 1 ? 's' : ''}</div>
          {rivalry.level >= 3 && (
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#d4af37', background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.35)', borderRadius: '3px', padding: '1px 6px', letterSpacing: '0.5px' }}>
              ⭐ BOOK THIS
            </div>
          )}
          <button onClick={onEdit} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', borderRadius: '3px', padding: '1px 6px', cursor: 'pointer', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem' }}>✏</button>
          <button onClick={onEnd} style={{ background: 'none', border: '1px solid rgba(231,76,60,0.3)', color: 'rgba(231,76,60,0.6)', borderRadius: '3px', padding: '1px 6px', cursor: 'pointer', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem' }}>END</button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 14px 14px', gap: '8px' }}>
        {renderSide(rivalry.side1 || [], 'SIDE 1')}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.4rem', color: '#e74c3c', textShadow: '0 0 20px rgba(231,76,60,0.6)' }}>VS</div>
          {isTeam && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>TAG</div>}
        </div>
        {renderSide(rivalry.side2 || [], 'SIDE 2')}
      </div>
      {/* History bandeau */}
      <div onClick={onView}
        style={{ borderTop: '1px solid ' + lvlColor + '22', padding: '6px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: lvlColor + '08', cursor: 'pointer', transition: 'background 0.15s' }}
        onMouseEnter={function(e) { e.currentTarget.style.background = lvlColor + '18'; }}
        onMouseLeave={function(e) { e.currentTarget.style.background = lvlColor + '08'; }}>
        <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: lvlColor, letterSpacing: '1.5px' }}>📋 VIEW HISTORY</span>
        {(rivalry.events || []).length > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.3)' }}>({rivalry.events.length} events)</span>}
      </div>
    </div>
  );
};

const RivalryPickerPopup = ({ rivalry, roster, currentWeek, onClose, onSave, champions }) => {
  const [type, setType] = useState(rivalry?.type || 'solo');
  const [level, setLevel] = useState(rivalry?.level || 1);
  const [side1, setSide1] = useState(rivalry?.side1 || []);
  const [side2, setSide2] = useState(rivalry?.side2 || []);
  const [storylineId, setStorylineId] = useState(rivalry?.storylineId || null);
  const [titleKey, setTitleKey] = useState(rivalry?.titleKey || null);
  const [pickingFor, setPickingFor] = useState(null); // { side: 1|2, idx: 0|1 }

  const memberCount = type === 'solo' ? 1 : 2;

  const setMember = (side, idx, wrestler) => {
    if (side === 1) { const s = [...side1]; s[idx] = wrestler ? wrestler.wrestler_id : null; setSide1(s.filter(Boolean)); }
    else { const s = [...side2]; s[idx] = wrestler ? wrestler.wrestler_id : null; setSide2(s.filter(Boolean)); }
    setPickingFor(null);
  };

  const getMember = (side, idx) => {
    const ids = side === 1 ? side1 : side2;
    const id = ids[idx];
    return id ? roster.find(w => w.wrestler_id === id || w.wrestler_id === Number(id)) : null;
  };

  const isValid = side1.length >= memberCount && side2.length >= memberCount;

  const SlotBtn = ({ side, idx }) => {
    const member = getMember(side, idx);
    return (
      <div onClick={() => setPickingFor({ side, idx })}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', minWidth: '160px' }}>
        {member ? (
          <>
            <img src={getWrestlerRender(member) || placeholder} alt="" style={{ width: '28px', height: '36px', objectFit: 'contain', objectPosition: 'top' }} />
            <div>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', color: '#fff' }}>{getWrestlerNameUpper(member, 'display')}</div>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.3)' }}>{member.fight_class}</div>
            </div>
            <button onClick={e => { e.stopPropagation(); setMember(side, idx, null); }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(231,76,60,0.5)', cursor: 'pointer', fontSize: '0.6rem' }}>✕</button>
          </>
        ) : (
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '1px' }}>+ SELECT</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(8,6,0,0.97)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '3px', color: '#e74c3c' }}>{rivalry ? 'EDIT RIVALRY' : 'NEW RIVALRY'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Type */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '6px' }}>TYPE</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['solo', 'team'].map(t => (
                <button key={t} onClick={() => { setType(t); setSide1([]); setSide2([]); }}
                  style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', letterSpacing: '1px', background: type === t ? 'rgba(231,76,60,0.12)' : 'transparent', border: '1px solid ' + (type === t ? 'rgba(231,76,60,0.6)' : 'rgba(255,255,255,0.15)'), color: type === t ? '#e74c3c' : 'rgba(255,255,255,0.4)', padding: '6px 20px', borderRadius: '4px', cursor: 'pointer' }}>
                  {t === 'solo' ? '1 VS 1' : '2 VS 2'}
                </button>
              ))}
            </div>
          </div>
          {/* Storyline preset picker */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '6px' }}>STORYLINE <span style={{ color: 'rgba(255,255,255,0.2)' }}>· OPTIONAL</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', maxHeight: '200px', overflowY: 'auto', padding: '4px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div onClick={function() { setStorylineId(null); }}
                style={{ padding: '8px 10px', background: !storylineId ? 'rgba(155,89,182,0.12)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (!storylineId ? 'rgba(155,89,182,0.5)' : 'rgba(255,255,255,0.07)'), borderRadius: '6px', cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: !storylineId ? '#9b59b6' : 'rgba(255,255,255,0.5)', letterSpacing: '1px' }}>NONE</div>
              </div>
              {STORYLINES.filter(function(s) {
                return type === 'team' ? s.category === 'tag' : s.category === 'solo';
              }).map(function(s) {
                var isSelected = storylineId === s.id;
                return (
                  <div key={s.id} onClick={function() { setStorylineId(isSelected ? null : s.id); }}
                    style={{ padding: '8px 10px', background: isSelected ? 'rgba(155,89,182,0.12)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (isSelected ? 'rgba(155,89,182,0.5)' : 'rgba(255,255,255,0.07)'), borderRadius: '6px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '0.65rem' }}>{s.icon}</span>
                      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', letterSpacing: '0.5px', color: isSelected ? '#9b59b6' : '#fff' }}>{s.name.toUpperCase()}</div>
                    </div>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.3 }}>{s.description.replace(/\{[A-Z0-9]+\}/g, '…')}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Title selector — only when storyline requiresTitle */}
          {storylineId && STORYLINES.find(s => s.id === storylineId)?.requiresTitle && (
            <div>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '6px' }}>
                CHAMPIONSHIP <span style={{ color: '#e74c3c', fontSize: '0.38rem' }}>· REQUIRED</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {((champions && champions.slots) || []).map(function(slot) {
                  var titleName = (slot.titleOverride && slot.titleOverride.name) || slot.label || slot.key;
                  var isSelected = titleKey === slot.key;
                  return (
                    <div key={slot.key} onClick={() => setTitleKey(isSelected ? null : slot.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', background: isSelected ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (isSelected ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.07)') }}>
                      {slot.titleOverride && <img src={(slot.titleOverride.render || (process.env.PUBLIC_URL + '/titles/' + slot.titleOverride.id + '-render.png'))} alt="" style={{ height: '24px', objectFit: 'contain', filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.8))' }} onError={e => { e.target.style.display='none'; }} />}
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: isSelected ? '#d4af37' : 'rgba(255,255,255,0.6)', flex: 1 }}>{titleName}</div>
                      {isSelected && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: '#d4af37' }}>✓</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Level */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '6px' }}>LEVEL</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[1,2,3,4].map(l => (
                <button key={l} onClick={() => setLevel(l)}
                  style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1px', background: level === l ? RIVALRY_LEVEL_COLORS[l] + '22' : 'transparent', border: '1px solid ' + (level === l ? RIVALRY_LEVEL_COLORS[l] : 'rgba(255,255,255,0.12)'), color: level === l ? RIVALRY_LEVEL_COLORS[l] : 'rgba(255,255,255,0.3)', padding: '6px 16px', borderRadius: '4px', cursor: 'pointer' }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: RIVALRY_LEVEL_COLORS[level], marginTop: '4px' }}>{RIVALRY_LEVEL_LABELS[level]}</div>
          </div>
          {/* Sides */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>SIDE 1</div>
              {Array.from({ length: memberCount }).map((_, i) => <SlotBtn key={i} side={1} idx={i} />)}
            </div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', color: '#e74c3c', textAlign: 'center' }}>VS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>SIDE 2</div>
              {Array.from({ length: memberCount }).map((_, i) => <SlotBtn key={i} side={2} idx={i} />)}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div style={{ padding: '12px 22px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>CANCEL</button>
          <button onClick={() => isValid && onSave({ type, level, side1, side2, storylineId: storylineId, titleKey: titleKey, storylineVariantByLevel: rivalry?.storylineVariantByLevel || {}, startWeek: rivalry?.startWeek || currentWeek, weeks: rivalry?.weeks || 0 })}
            disabled={!isValid}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid ' + (isValid ? 'rgba(231,76,60,0.6)' : 'rgba(255,255,255,0.12)'), color: isValid ? '#e74c3c' : 'rgba(255,255,255,0.2)', padding: '8px 18px', borderRadius: '4px', cursor: isValid ? 'pointer' : 'not-allowed' }}>CONFIRM</button>
        </div>
      </div>

      {/* Wrestler picker */}
      {pickingFor && (
        <SuperstarPicker
          roster={roster}
          alreadyPicked={[
            ...(pickingFor.side === 1 ? side2 : side1).filter(Boolean),
          ]}
          onSelect={function(w) { setMember(pickingFor.side, pickingFor.idx, w); setPickingFor(null); }}
          onClose={() => setPickingFor(null)}
          title={'SELECT — SIDE ' + pickingFor.side}
          showSynergy={false}
          rivalries={[]}
        />
      )}
    </div>
  );
};

const RivalriesTab = ({ gameData, onBack, onUpdateGameData }) => {
  const roster = gameData?.roster || [];
  const rivalriesData = gameData?.rivalries || [];
  const currentWeek = gameData?.week || 1;
  const brandColor = BRAND_COLORS[gameData?.brand] || '#d4af37';

  const [rivalries, setRivalries] = useState(rivalriesData);
  const [pastRivalries, setPastRivalries] = useState(gameData?.pastRivalries || []);
  const [showPicker, setShowPicker] = useState(false);
  const [editingRivalry, setEditingRivalry] = useState(null);
  const [viewingRivalry, setViewingRivalry] = useState(null);
  const [notes, setNotes] = useState(gameData?.rivalryNotes || []);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [activeSection, setActiveSection] = useState('active');

  const saveAll = (newRivalries, newPast, newNotes) => {
    onUpdateGameData && onUpdateGameData({ ...gameData, rivalries: newRivalries, pastRivalries: newPast, rivalryNotes: newNotes });
  };

  const handleSaveRivalry = (data) => {
    let newRivalries;
    if (editingRivalry && editingRivalry._idx !== undefined) {
      newRivalries = rivalries.map((r, i) => i === editingRivalry._idx ? { ...data, id: r.id } : r);
    } else {
      var initialVariantMap = data.storylineVariantByLevel || {};
      if (data.storylineId && (!initialVariantMap[1] && initialVariantMap[1] !== 0)) {
        var stFound = STORYLINES.find(function(s) { return s.id === data.storylineId; });
        if (stFound) {
          var ph1 = stFound.phases.find(function(p) { return p.level === 1; });
          if (ph1 && ph1.variants && ph1.variants.length > 0) {
            initialVariantMap = { ...initialVariantMap, 1: Math.floor(Math.random() * ph1.variants.length) };
          }
        }
      }
      var createEv = {
        week: currentWeek,
        type: 'create',
        impact: 'create',
        rivalryLevel: 1,
        storylineId: data.storylineId || null,
        storylineVariantIdx: data.storylineId ? (initialVariantMap[1] ?? null) : null,
        participants: [],
        winner: null,
        noContest: false,
        rating: 0,
        matchType: 'normal',
        matchRule: '1v1',
        titleMatch: null,
        titleChange: false,
        isPromo: false,
        promoAction: null,
        callers: [],
        targets: [],
        notes: '',
      };
      newRivalries = [...rivalries, { ...data, id: Date.now(), storylineVariantByLevel: initialVariantMap, events: [createEv, ...(data.events || [])] }];
    }
    setRivalries(newRivalries);
    setShowPicker(false);
    setEditingRivalry(null);
    saveAll(newRivalries, pastRivalries, notes);
  };

  const handleEndRivalry = (idx) => {
    const ended = { ...rivalries[idx], endWeek: currentWeek, ended: true };
    const newRivalries = rivalries.filter((_, i) => i !== idx);
    const newPast = [ended, ...pastRivalries];
    setRivalries(newRivalries);
    setPastRivalries(newPast);
    saveAll(newRivalries, newPast, notes);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexShrink: 0 }}>
        <button onClick={onBack} style={{ ...S.btn, fontSize: '0.5rem', padding: '6px 14px' }}>← BACK</button>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', letterSpacing: '3px', color: '#fff' }}>RIVALRIES</div>
        <button onClick={() => { setEditingRivalry(null); setShowPicker(true); }}
          style={{ marginLeft: 'auto', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', letterSpacing: '1px', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.4)', color: '#e74c3c', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer' }}>
          + NEW RIVALRY
        </button>
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '16px', flexShrink: 0 }}>
        {[
          { key: 'active', label: 'ONGOING (' + rivalries.length + ')' },
          { key: 'past', label: 'PAST (' + pastRivalries.length + ')' },
          { key: 'notes', label: 'NOTES (' + notes.filter(n => !n.archived).length + ')' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveSection(tab.key)}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', letterSpacing: '1.5px', background: 'transparent', border: 'none', borderBottom: '2px solid ' + (activeSection === tab.key ? '#e74c3c' : 'transparent'), color: activeSection === tab.key ? '#fff' : 'rgba(255,255,255,0.35)', padding: '8px 20px', cursor: 'pointer', transition: 'all 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* ACTIVE */}
        {activeSection === 'active' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '14px', alignContent: 'flex-start' }}>
            {rivalries.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px' }}>
                No ongoing rivalries.
              </div>
            )}
            {rivalries.map((r, i) => (
              <RivalryCard key={r.id || i} rivalry={r} roster={roster} brandColor={brandColor} champions={gameData?.champions}
                onView={() => setViewingRivalry(r)}
                onEdit={() => { setEditingRivalry({ ...r, _idx: i }); setShowPicker(true); }}
                onEnd={() => handleEndRivalry(i)} />
            ))}
          </div>
        )}

        {/* PAST */}
        {activeSection === 'past' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pastRivalries.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px' }}>No past rivalries.</div>
            )}
            {pastRivalries.map((r, i) => {
              const s1 = (r.side1 || []).map(id => roster.find(w => w.wrestler_id === id || w.wrestler_id === Number(id))).filter(Boolean);
              const s2 = (r.side2 || []).map(id => roster.find(w => w.wrestler_id === id || w.wrestler_id === Number(id))).filter(Boolean);
              const lvlColor = RIVALRY_LEVEL_COLORS[r.level] || '#fff';
              return (
                <div key={i} style={{ background: 'rgba(8,8,8,0.7)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[1,2,3,4].map(l => <div key={l} style={{ width: '14px', height: '3px', borderRadius: '2px', background: l <= r.level ? lvlColor : 'rgba(255,255,255,0.08)' }} />)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.58rem', color: 'rgba(255,255,255,0.7)', letterSpacing: '1px' }}>
                        {s1.map(w => w.name || getWrestlerName(w, 'display') || '?').join(' & ')} <span style={{ color: '#e74c3c' }}>VS</span> {s2.map(w => w.name || getWrestlerName(w, 'display') || '?').join(' & ')}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '3px', alignItems: 'center' }}>
                        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)' }}>W{r.startWeek} → W{r.endWeek} · {r.weeks} wk{r.weeks > 1 ? 's' : ''}</div>
                        {r.finalWinner && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#d4af37' }}>👑 {r.finalWinner.name || getWrestlerName(r.finalWinner, 'display')}</div>}
                      </div>
                    </div>
                  </div>
                  {/* History bandeau */}
                  <div onClick={() => setViewingRivalry(r)}
                    style={{ borderTop: '1px solid ' + lvlColor + '22', padding: '5px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: lvlColor + '06', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = lvlColor + '15'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = lvlColor + '06'; }}>
                    <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: lvlColor, letterSpacing: '1.5px' }}>📋 VIEW HISTORY</span>
                    {(r.events || []).length > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: 'rgba(255,255,255,0.3)' }}>({r.events.length} events)</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* NOTES */}
        {activeSection === 'notes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button onClick={() => { setEditingNote({ text: '', tags: [], archived: false }); setShowNoteEditor(true); }}
              style={{ alignSelf: 'flex-start', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', letterSpacing: '1px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.35)', color: '#d4af37', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer', marginBottom: '4px' }}>
              + NEW NOTE
            </button>
            {notes.filter(n => !n.archived).length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)' }}>No notes yet.</div>
            )}
            {notes.map((note, i) => note.archived ? null : (
              <div key={i} style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.85)', lineHeight: '1.6', marginBottom: note.tags && note.tags.length > 0 ? '8px' : '0', whiteSpace: 'pre-wrap' }}>{note.text}</div>
                {note.tags && note.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {note.tags.map((tag, ti) => (
                      <div key={ti} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: '#d4af37', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '3px', padding: '2px 7px' }}>@ {tag}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setEditingNote({ ...note, _idx: i }); setShowNoteEditor(true); }}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '3px 8px', borderRadius: '3px', cursor: 'pointer' }}>✏ EDIT</button>
                  <button onClick={() => { const n = [...notes]; n[i] = { ...n[i], archived: true }; setNotes(n); saveAll(rivalries, pastRivalries, n); }}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', background: 'transparent', border: '1px solid rgba(231,76,60,0.25)', color: 'rgba(231,76,60,0.5)', padding: '3px 8px', borderRadius: '3px', cursor: 'pointer' }}>ARCHIVE</button>
                </div>
              </div>
            ))}
            {notes.some(n => n.archived) && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', marginBottom: '8px' }}>ARCHIVED</div>
                {notes.map((note, i) => !note.archived ? null : (
                  <div key={i} style={{ background: 'rgba(8,8,8,0.5)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px', opacity: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.5)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.text}</div>
                    <button onClick={() => { const n = [...notes]; n.splice(i, 1); setNotes(n); saveAll(rivalries, pastRivalries, n); }}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', background: 'transparent', border: '1px solid rgba(231,76,60,0.2)', color: 'rgba(231,76,60,0.4)', padding: '2px 7px', borderRadius: '3px', cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}>DELETE</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rivalry detail modal */}
      {viewingRivalry && (
        <RivalryDetailModal
          rivalry={viewingRivalry}
          roster={roster}
          brandColor={brandColor}
          onClose={() => setViewingRivalry(null)}
          champions={gameData?.champions}
        />
      )}

      {/* Rivalry picker popup */}
      {showPicker && (
        <RivalryPickerPopup
          rivalry={editingRivalry}
          roster={roster}
          currentWeek={currentWeek}
          onClose={() => { setShowPicker(false); setEditingRivalry(null); }}
          onSave={handleSaveRivalry}
          champions={gameData?.champions}
        />
      )}

      {/* Note editor popup */}
      {showNoteEditor && (
        <NoteEditorPopup
          note={editingNote}
          roster={roster}
          onClose={() => { setShowNoteEditor(false); setEditingNote(null); }}
          onSave={(note) => {
            let newNotes;
            if (editingNote && editingNote._idx !== undefined) {
              newNotes = notes.map((n, i) => i === editingNote._idx ? note : n);
            } else {
              newNotes = [...notes, note];
            }
            setNotes(newNotes);
            setShowNoteEditor(false);
            setEditingNote(null);
            saveAll(rivalries, pastRivalries, newNotes);
          }}
        />
      )}
    </div>
  );
};

const NoteEditorPopup = ({ note, roster, onClose, onSave }) => {
  const [text, setText] = useState(note?.text || '');
  const [tags, setTags] = useState(note?.tags || []);
  const [tagSearch, setTagSearch] = useState('');
  const [showTagPicker, setShowTagPicker] = useState(false);

  const filteredRoster = roster.filter(w => wrestlerMatchesSearch(w, tagSearch) && w.status === 'active');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(8,6,0,0.97)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '12px', width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#d4af37' }}>NOTE</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <textarea
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '10px 12px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none', resize: 'vertical', minHeight: '120px', lineHeight: '1.6' }}
            placeholder="Write your note here..."
            value={text} onChange={e => setText(e.target.value)} autoFocus />
          {/* Tags */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '6px' }}>TAGGED SUPERSTARS</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
              {tags.map((tag, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#d4af37', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '3px', padding: '2px 8px' }}>
                  @ {tag}
                  <button onClick={() => setTags(tags.filter((_, ti) => ti !== i))} style={{ background: 'none', border: 'none', color: 'rgba(231,76,60,0.5)', cursor: 'pointer', fontSize: '0.5rem', padding: '0 0 0 4px' }}>✕</button>
                </div>
              ))}
              <button onClick={() => setShowTagPicker(p => !p)}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', background: 'transparent', border: '1px dashed rgba(212,175,55,0.3)', color: 'rgba(212,175,55,0.5)', padding: '2px 10px', borderRadius: '3px', cursor: 'pointer' }}>+ TAG</button>
            </div>
            {showTagPicker && (
              <div style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '8px', maxHeight: '160px', overflowY: 'auto' }}>
                <input style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', padding: '5px 8px', color: '#fff', fontSize: '0.5rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '100%', marginBottom: '6px' }}
                  placeholder="Search..." value={tagSearch} onChange={e => setTagSearch(e.target.value)} />
                {filteredRoster.slice(0, 8).map((w, i) => (
                  <div key={i} onClick={() => { if (!tags.includes(getWrestlerName(w, 'display'))) setTags(p => [...p, getWrestlerName(w, 'display')]); setShowTagPicker(false); setTagSearch(''); }}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: tags.includes(getWrestlerName(w, 'display')) ? '#d4af37' : '#fff', padding: '4px 6px', borderRadius: '3px', cursor: 'pointer', background: tags.includes(getWrestlerName(w, 'display')) ? 'rgba(212,175,55,0.08)' : 'transparent' }}>
                    {getWrestlerName(w, 'display')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer' }}>CANCEL</button>
          <button onClick={() => text.trim() && onSave({ text: text.trim(), tags, archived: false })}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(212,175,55,0.5)', color: '#d4af37', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer' }}>SAVE</button>
        </div>
      </div>
    </div>
  );
};

export default function RosterTab({ gameData, brand, onUpdateGameData, initialSection, onBackToHub, confirmDestructive }) {
  const [section, setSection] = useState(initialSection || null);
  // Reset to hub when initialSection changes to null
  React.useEffect(function() { if (!initialSection) setSection(null); }, [initialSection]);

  var handleBack = function() { if (onBackToHub) { onBackToHub(); } else { setSection(null); } };
  if (!section) return <RosterHub onSelect={setSection} gameData={gameData} />;
  if (section === 'actual') return (
    <ActualRosterPage
      roster={gameData?.roster || []}
      brand={brand}
      gameData={gameData}
      onBack={handleBack}
      onUpdateRoster={onUpdateGameData}
      confirmDestructive={confirmDestructive !== false}
    />
  );
  if (section === 'rivalries') return (
    <RivalriesTab
      gameData={gameData}
      onBack={handleBack}
      onUpdateGameData={onUpdateGameData}
    />
  );
  if (section === 'championships') return (
    <ChampionshipsTab
      gameData={gameData}
      onBack={handleBack}
      onUpdateGameData={onUpdateGameData}
    />
  );
  if (section === 'recruitment') return (
    <RecruitmentTab
      gameData={gameData}
      onBack={handleBack}
      onUpdateGameData={onUpdateGameData}
    />
  );
  return null;
}
